/**
 * Flow scene — 3D tunnel viewport.
 *
 * First-person tunnel with 4 lanes glued to the floor. Notes scroll toward
 * the camera as `currentBeat` advances. Click on a lane to place a note,
 * click an existing note to remove it. Mouse-wheel scrubs the playhead in
 * beat-step increments (with an audible tick). When a note crosses the
 * strike bar during playback, a per-lane hit sound fires and a burst of
 * sparks shoots upward at the strike point.
 *
 * Trails: when the note-type brush is set to "trail", clicking the floor
 * places a START note; the next click on the floor places the END note
 * and links the two with a colored ribbon sitting flat on the floor.
 * The first note's `duration_beats` records the gap.
 *
 * Workaround: r3f v9 needs a real (trusted) browser event before its first
 * paint. Until the user clicks the "Enter the highway" overlay, the scene
 * stays hidden behind it.
 */

import React, { Suspense, useRef, useMemo, useCallback, useEffect, useState } from 'react';
import { Canvas, useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import StepLines from '../../components/three/StepLines';
import { PERF } from '../../perf/adaptiveBudget';
import AdaptivePerfGovernor from '../../perf/AdaptivePerfGovernor';
import { useScaledDpr } from '../../perf/useScaledDpr';
import PotatoFrameCap from '../../perf/PotatoFrameCap';
import { useEditorStore, DEFAULT_PERFORMANCE } from '../../store/editorStore';
import type { PerfFlowFx } from '../../store/editorStore';
import { Html, Line, OrbitControls, RoundedBox, useFBX } from '@react-three/drei';
import * as THREE from 'three';
import { mergeBufferGeometries } from 'three-stdlib';
import {
  useFlowStore, liveLaneHeld,
  playTestHitIds, playTestSparkQueue, playTestHolds,
} from './flowStore';
import {
  FLOW_LANE_COLORS,
  FLOW_LANE_COUNT,
  laneCountFor,
  arrowDirectionFromType,
  type FlowLaneIndex,
  type FlowNote,
  type FlowTrailWaypoint,
} from './types';
import {
  TUNNEL_LENGTH, LANE_WIDTH, STRIKE_Z, VISIBLE_BEATS_AHEAD,
  NOTE_HEIGHT, NOTE_LENGTH, FLOOR_Y, TUBE_THRESHOLD, SNAP_OPTIONS, deckWidthFor,
  EDIT_WINDOW_BEATS,
} from './flowSceneConstants';
import {
  type Spark, type SparkSpawnOptions, MAX_SPARKS, emptySpark, spawnSparks, applyTurbulence,
} from './flowSparks';
import type { FlowParticleSettings } from './flowSettings';
import { playScrollTick, playPlaceSound, playDeleteSound } from './flowSounds';
import { getPosition, getPeaks } from './flowAudioEngine';
import { probe } from './flowSyncProbe';
import { emitFlowHit } from './flowHitBus';
import { BUILTIN_CAMERA } from './flowCameraPresets';
import { analyzeFlowDifficulty } from './flowDifficultyAnalyzer';
import { color, font } from '../../components/ui/theme';
import { useTheme } from '../../components/ui/themeStore';
import { accentHex, tintHex } from '../../components/ui/themeColors';

/**
 * Pill / stadium shape for the lane keypads at the strike line.
 * Width = LANE_WIDTH * 0.85, Height = 0.5 world units, radius = 0.25 (= H/2).
 * Built once at module load; reused across all keypad meshes.
 */
const KEYPAD_PILL_SHAPE = (() => {
  const W  = LANE_WIDTH * 0.85;
  const H  = 0.5;
  const r  = H / 2;
  const hw = Math.max(0, W / 2 - r);   // half-length of the straight body
  const s  = new THREE.Shape();
  s.moveTo(-hw, -r);
  s.lineTo( hw, -r);
  s.absarc( hw, 0, r, -Math.PI / 2,      Math.PI / 2,      false); // right cap
  s.lineTo(-hw,  r);
  s.absarc(-hw, 0, r,  Math.PI / 2, 3 * Math.PI / 2, false); // left cap
  s.closePath();
  return s;
})();

/** Same pill as KEYPAD_PILL_SHAPE but as a closed polyline in the XZ plane
 *  (lies flat), for rendering the keypad as a smooth outline instead of a
 *  filled shape. High segment count so the rounded caps don't look faceted. */
const KEYPAD_OUTLINE_POINTS: [number, number, number][] = (() => {
  const W = LANE_WIDTH * 0.85, H = 0.5, r = H / 2, hw = Math.max(0, W / 2 - r);
  const segs = 40;
  const pts: [number, number, number][] = [];
  for (let i = 0; i <= segs; i++) { const a = -Math.PI / 2 + (Math.PI * i) / segs; pts.push([hw + r * Math.cos(a), 0, r * Math.sin(a)]); }
  for (let i = 0; i <= segs; i++) { const a = Math.PI / 2 + (Math.PI * i) / segs; pts.push([-hw + r * Math.cos(a), 0, r * Math.sin(a)]); }
  pts.push(pts[0]);
  return pts;
})();


/**
 * Convert a snap-division constant (4, 8, 16, 32, 64) into the size of one
 * grid step expressed in beats. With snapDivision = 4 we move a full beat
 * per wheel tick; snapDivision = 16 → quarter-of-a-beat (16th notes).
 */
function snapStepBeats(snapDivision: number): number {
  return 4 / Math.max(snapDivision, 1);
}

// Active lane count for the difficulty on screen (3 or 4). Set from the store
// each render by SceneContents (like NJS_SCALE) so the pure geometry helpers
// can spread lanes without threading the value through every call site. Note
// SIZE stays fixed (LANE_WIDTH); only the lane CENTRES spread to fill the width.
let CUR_LANE_COUNT = FLOW_LANE_COUNT;
/** True while a Play Test run is live. Mirrored from the store once per frame
 *  so per-note components can check it without subscribing. */
let PLAY_TEST_ON = false;
/** Module mirror of the ACTIVE preset's Flow effect gates (user-editable in the
 *  Performance settings tab), for the per-frame code paths that can't subscribe
 *  to the store (TrailTube's shader gate, spark spawning). Written once per
 *  frame in SceneContents' useFrame alongside the other mirrors. */
let ACTIVE_FX: PerfFlowFx = DEFAULT_PERFORMANCE.auto.flowFx;
/** How far a keypad pill rises while its lane key is held (world units ≈ 10px
 *  on screen at the default camera) — the "which key am I pressing" tell. */
const KEYPAD_PRESS_LIFT = 0.12;
/** Deck width for CUR_LANE_COUNT — 4 world units up to 4 lanes, wider at 5 so
 *  the lanes don't crowd (see deckWidthFor). Kept in sync with CUR_LANE_COUNT. */
let CUR_DECK_W = deckWidthFor(CUR_LANE_COUNT);

function laneCenterX(lane: FlowLaneIndex | number): number {
  // Mirror across the X axis so lane 0 (green) renders on the user's
  // visual LEFT — Three.js's default camera-up flips +X to the user's
  // left when looking down +Z, so we compensate here. Lanes are spread
  // evenly across the deck width for the active lane count.
  const spacing = CUR_DECK_W / Math.max(1, CUR_LANE_COUNT);
  return CUR_DECK_W / 2 - spacing * (lane + 0.5);
}

// Note Jump Speed multiplier (editor view). Set from the store each render by
// SceneContents BEFORE any beatToZ call, so the pure helper can scale spacing
// without threading the value through ~15 call sites. 1 = base spacing.
let NJS_SCALE = 1;

/** World units per beat at the current NJS. */
function unitPerBeat(): number {
  return (TUNNEL_LENGTH / VISIBLE_BEATS_AHEAD) * NJS_SCALE;
}

function beatToZ(beat: number, currentBeat: number): number {
  const beatsFromStrike = beat - currentBeat;
  return STRIKE_Z + (beatsFromStrike / VISIBLE_BEATS_AHEAD) * TUNNEL_LENGTH * NJS_SCALE;
}

// ── Spark particle system ────────────────────────────────────────────────────
// A pool of MAX_SPARKS particles drawn as ONE InstancedMesh. When a note
// crosses the strike we grab free slots, launch them, and let useFrame age
// them; each frame the LIVE ones are compacted to the front of the instance
// buffer so idle pool slots cost nothing at all. That's what makes a 1000-
// particle burst affordable — as separate meshes it would be 1000 draw calls.

/** Scratch matrix reused for every instance write — never allocate per frame. */
const sparkMat4 = new THREE.Matrix4();

/** Sparks rest on the deck rather than sinking through it. */
const SPARK_FLOOR = FLOOR_Y + 0.02;
const SPARK_BOUNCE = 0.35;

/** Pack the user's particle settings into a spawn call. */
function sparkOpts(ps: FlowParticleSettings, count: number): SparkSpawnOptions {
  return {
    count,
    lifetime: ps.sparkLifetime,
    speedMin: ps.sparkSpeed,
    speedMax: ps.sparkSpeedMax ?? ps.sparkSpeed,
    shape: ps.sparkShape ?? 'box',
    spread: ps.sparkSpread ?? 0.35,
  };
}

// ── Arrow glyphs for directional notes ───────────────────────────────────────

/**
 * Build a triangle shape pointing in +X (will be rotated as needed). The
 * triangle lies flat on the floor when rotated by -π/2 around X. Drawn as
 * a `shapeGeometry` so it stays cheap (3 verts).
 */
/**
 * Classic "arrow with a shaft" polygon, apex at local +X. Traced as a single
 * closed path so it renders cheaply as one `shapeGeometry`. Wing tips flare
 * past the shaft so the head still reads as a triangle from a distance.
 *
 *   apex (0.22, 0)
 *      \
 *      /\
 *     /  \
 *    /____\___
 *   |__   shaft   __|
 *      ‾‾‾‾‾‾‾‾‾‾‾
 */
const ARROW_SHAPE = (() => {
  const s = new THREE.Shape();
  s.moveTo(0.22, 0);            // apex
  s.lineTo(0.02, 0.16);         // top wing tip
  s.lineTo(0.02, 0.06);         // step down to shaft top
  s.lineTo(-0.20, 0.06);        // shaft back top
  s.lineTo(-0.20, -0.06);       // shaft back bottom
  s.lineTo(0.02, -0.06);        // step up to bottom wing
  s.lineTo(0.02, -0.16);        // bottom wing tip
  s.closePath();                 // back to apex
  return s;
})();

/**
 * Extrude config — the depth becomes the arrow's thickness "into" the
 * banner plane (i.e. the chevron now sits proud off the floor). A small
 * bevel softens the rim so the scene's directional light catches a
 * highlight along the edges and the arrow reads unambiguously as 3D.
 *
 * Values were tuned in-editor via the (now-removed) ArrowDebugPanel.
 */
const ARROW_EXTRUDE_OPTS = {
  depth: 0.07,
  bevelEnabled: true,
  bevelSize: 0.014,
  bevelThickness: 0.014,
  bevelSegments: 1,
  curveSegments: 1,
} as const;

/** Vertical lift of the arrow above its parent note (Y offset). */
const ARROW_POSITION_Y = 0.30;
/** Banner tilt around world X (90° → arrow stands straight up off the note). */
const ARROW_TILT_X = Math.PI / 2;
/** Uniform scale applied to the arrow glyph. */
const ARROW_SCALE = 0.70;

/**
 * Renders a flat white arrow glyph hovering just above the parent note.
 * The lane X-axis is mirrored (green/lane 0 sits at world +X but appears
 * on the visual LEFT), so the arrow apexes use the visual convention:
 *   - 'left'    apex points DIAGONAL up-left  (↖, midway between up & left)
 *   - 'center'  apex points UP-screen         (↑, world +Z, into the tunnel)
 *   - 'right'   apex points DIAGONAL up-right (↗, midway between up & right)
 *
 * Diagonals match Fortnite-Festival-style strum lanes more closely than
 * pure horizontal arrows.
 */
function ArrowGlyph({
  direction,
  dimmed = false,
}: {
  direction: 'left' | 'center' | 'right';
  /** Drop the arrow to 25% when the parent note is marked as cut. */
  dimmed?: boolean;
}) {
  // The mesh rotation [-π/2, 0, yRot] lays the base shape flat in the
  // local XY plane and then spins it via Rz(yRot). When the outer group
  // tilts everything by Rx(+π/4), the net world transform of the apex
  // (local +X) becomes Rx(-π/4) * Rz(yRot) * (+X). Working backward, the
  // direction that lands on (0, +0.707, -0.707) — UP and TOWARD the
  // camera — is yRot = +π/2.
  //
  //   yRot = +π/4   → diagonal ↖ (up + visual LEFT, mirrored on +X)
  //   yRot = +π/2   → ↑ (toward camera, lifted up)
  //   yRot = +3π/4  → diagonal ↗ (up + visual RIGHT)
  let yRot = Math.PI / 2;
  if (direction === 'left') yRot = Math.PI / 4;
  else if (direction === 'right') yRot = (3 * Math.PI) / 4;
  // Outer group tilts the whole arrow 45° around world X so the apex
  // lifts off the floor — the arrow reads as a banner standing up
  // toward the player instead of a sticker lying on the lane. The inner
  // mesh extrudes the chevron profile and uses a lit (Lambert) material
  // so the scene's directional + point lights shade the front face, the
  // sides, and the back differently — that's what sells the 3D feel.
  //
  // We re-center the extruded prism along its depth so the geometric
  // mid-plane sits at the group origin (instead of one face being flush
  // with it). That keeps the banner visually balanced when tilted.
  return (
    <group
      position={[0, ARROW_POSITION_Y, 0]}
      rotation={[ARROW_TILT_X, 0, 0]}
      scale={[ARROW_SCALE, ARROW_SCALE, ARROW_SCALE]}
    >
      <mesh
        rotation={[-Math.PI / 2, 0, yRot]}
        position={[0, 0, -ARROW_EXTRUDE_OPTS.depth / 2]}
        raycast={() => null}
      >
        <extrudeGeometry args={[ARROW_SHAPE, ARROW_EXTRUDE_OPTS]} />
        <meshLambertMaterial
          color="#F8F4FF"
          emissive="#1a0a30"
          emissiveIntensity={0.45}
          transparent={dimmed}
          opacity={dimmed ? 0.25 : 1}
        />
      </mesh>
    </group>
  );
}

// ── Camera controller ───────────────────────────────────────────────────────
// Each preset view (default/left/right/custom) is a position+rotation pose:
// the user-edited override from the store if present, else the built-in pose
// (BUILTIN_CAMERA). Editing the Camera sliders moves the live camera when that
// view is active. 'free' hands control to OrbitControls.

function CameraController() {
  const { camera } = useThree();
  const cameraView = useFlowStore(s => s.cameraView);
  const dPos = useFlowStore(s => s.defaultCameraPosition);
  const dRot = useFlowStore(s => s.defaultCameraRotation);
  const lPos = useFlowStore(s => s.leftCameraPosition);
  const lRot = useFlowStore(s => s.leftCameraRotation);
  const rPos = useFlowStore(s => s.rightCameraPosition);
  const rRot = useFlowStore(s => s.rightCameraRotation);
  const cPos = useFlowStore(s => s.customCameraPosition);
  const cRot = useFlowStore(s => s.customCameraRotation);
  const saveReq = useFlowStore(s => s.saveCameraRequest);
  const storeCustomCamera = useFlowStore(s => s.storeCustomCamera);

  // Apply the active view's pose whenever it (or its override) changes.
  useEffect(() => {
    if (cameraView === 'free') return;
    const view: 'default' | 'left' | 'right' | 'custom' =
      cameraView === 'left' || cameraView === 'right' || cameraView === 'custom' ? cameraView : 'default';
    const ovPos = view === 'default' ? dPos : view === 'left' ? lPos : view === 'right' ? rPos : cPos;
    const ovRot = view === 'default' ? dRot : view === 'left' ? lRot : view === 'right' ? rRot : cRot;
    const b = BUILTIN_CAMERA[view];
    const pos = ovPos ?? b.pos;
    const rot = ovRot ?? b.rot;
    camera.position.set(pos[0], pos[1], pos[2]);
    camera.rotation.set(rot[0], rot[1], rot[2]);
  }, [cameraView, camera, dPos, dRot, lPos, lRot, rPos, rRot, cPos, cRot]);

  // When the sidebar's "Save View" button bumps saveCameraRequest, capture
  // the camera's current pose into the store and switch to 'custom'.
  const prevSaveReq = useRef(saveReq);
  useEffect(() => {
    if (saveReq === prevSaveReq.current) return;
    prevSaveReq.current = saveReq;
    storeCustomCamera(
      [camera.position.x, camera.position.y, camera.position.z],
      [camera.rotation.x, camera.rotation.y, camera.rotation.z],
    );
  }, [saveReq, camera, storeCustomCamera]);

  if (cameraView === 'free') {
    return <OrbitControls makeDefault target={[0, FLOOR_Y + 0.3, 8]} />;
  }
  return null;
}

// ── Selection floor overlay ──────────────────────────────────────────────────
/**
 * White semi-transparent rectangle on the 3D floor marking the time range of
 * selected notes. Also draws a marching-ants dashed border animated via
 * useFrame so we never trigger React re-renders for the animation.
 *
 * Must be mounted INSIDE the worldRef group so it scrolls with the notes.
 */
// Marching-ants dash geometry + march speed (world units / second).
const DASH_SIZE = 0.18;
const GAP_SIZE = 0.10;
const DASH_SPEED = 0.9;

function SelectionFloorOverlay() {
  // Subscribe directly to the store so any note-move or selection change
  // causes an immediate re-render — no prop-drilling lag.
  const chart       = useFlowStore(s => s.chart);
  const selectedIds = useFlowStore(s => s.selection.noteIds);
  const flowNjs     = useFlowStore(s => s.flowNjs);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lineRef = useRef<any>(null);

  const bounds = useMemo(() => {
    if (selectedIds.size === 0) return null;
    let minBeat = Infinity, maxBeat = -Infinity;
    for (const note of chart.notes) {
      if (!selectedIds.has(note.id)) continue;
      minBeat = Math.min(minBeat, note.time_beats);
      const endBeat = note.duration_beats > 0
        ? note.time_beats + note.duration_beats
        : note.time_beats;
      maxBeat = Math.max(maxBeat, endBeat);
    }
    if (minBeat === Infinity) return null;
    // Give single-beat notes at least half a beat of width so the outline is visible.
    const safeMax = Math.max(minBeat + 0.5, maxBeat);
    return {
      z1: beatToZ(minBeat, 0),
      z2: beatToZ(safeMax, 0),
    };
  }, [chart.notes, selectedIds, flowNjs]);

  // Animate the dashed border offset each frame — mutates the LineMaterial
  // directly so no React re-renders are needed.
  //
  // Two things keep it smooth:
  //   1. Delta-time advance (units/sec) so the march speed is constant
  //      regardless of frame rate.
  //   2. Wrap by exactly ONE dash period (dashSize + gapSize) instead of 1.0.
  //      The pattern repeats every period, so wrapping there is seamless;
  //      wrapping at 1.0 jumped the pattern by a fractional period each cycle,
  //      which is what caused the visible stutter.
  useFrame((_, delta) => {
    const line = lineRef.current;
    if (!line || !bounds) return;
    const mat = line.material;
    if (mat && 'dashOffset' in mat) {
      const period = DASH_SIZE + GAP_SIZE;
      // clamp delta so a stalled tab doesn't make it leap on resume
      const d = Math.min(delta, 0.05);
      mat.dashOffset = (mat.dashOffset - DASH_SPEED * d) % period;
    }
  });

  if (!bounds) return null;

  const { z1, z2 } = bounds;
  const hw  = CUR_DECK_W / 2;
  const inset = 0.05; // px away from the lane edge
  const y   = FLOOR_Y + 0.025; // above floor grid-lines (max 0.022)

  const centerZ  = (z1 + z2) / 2;
  const fillLen  = Math.max(0.05, z2 - z1);

  // Closed rectangle of 5 points (last == first) for the dashed line.
  const borderPoints: [number, number, number][] = [
    [-hw + inset, y, z1 + inset],
    [ hw - inset, y, z1 + inset],
    [ hw - inset, y, z2 - inset],
    [-hw + inset, y, z2 - inset],
    [-hw + inset, y, z1 + inset],
  ];

  return (
    <>
      {/* White fill at ~10% opacity */}
      <mesh
        position={[0, y - 0.001, centerZ]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={() => null}
      >
        <planeGeometry args={[CUR_DECK_W - inset * 2, fillLen]} />
        <meshBasicMaterial
          color="#FFFFFF"
          transparent
          opacity={0.10}
          depthWrite={false}
        />
      </mesh>

      {/* Marching-ants border */}
      <Line
        ref={lineRef}
        points={borderPoints}
        color="#FFFFFF"
        lineWidth={1.5}
        dashed
        dashSize={DASH_SIZE}
        gapSize={GAP_SIZE}
        dashOffset={0}
        transparent
        opacity={0.80}
        raycast={() => null}
      />
    </>
  );
}

// ── Difficulty complexity overlay (3D floor) ────────────────────────────────
/**
 * Translucent floor bands marking sections too hard for the active difficulty
 * — the 3D twin of the timeline's amber/red bands. Amber = a touch hot, red =
 * clearly over the tier. Uses learned per-tier envelopes when available.
 *
 * Mounted INSIDE the worldRef group so the bands scroll with the notes.
 */
// Teal floor band marking the right-drag LOOP region (start..end beats).
// Mounted inside worldRef so it scrolls with the runway.
function LoopFloorOverlay() {
  const loop = useFlowStore(s => s.loopRegion);
  if (!loop) return null;
  const z1 = beatToZ(loop.startBeat, 0);
  const z2 = beatToZ(loop.endBeat, 0);
  const len = Math.max(0.05, z2 - z1);
  const centerZ = (z1 + z2) / 2;
  return (
    <>
      <mesh position={[0, FLOOR_Y + 0.02, centerZ]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
        <planeGeometry args={[CUR_DECK_W, len]} />
        <meshBasicMaterial color="#2DD4BF" transparent opacity={0.16} side={THREE.DoubleSide} />
      </mesh>
      {[z1, z2].map((z, i) => (
        <mesh key={i} position={[0, FLOOR_Y + 0.03, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
          <planeGeometry args={[CUR_DECK_W, 0.08]} />
          <meshBasicMaterial color="#2DD4BF" transparent opacity={0.85} />
        </mesh>
      ))}
    </>
  );
}

// Mirrors the white "section" band drawn in the 2D timeline onto the 3D
// highway floor, so a section selected for the copilot ("Map this section")
// is visible in both places — even when it spans an empty stretch with no
// notes. Reads `selectedSection` (in beats), independent of note selection.
function SectionFloorOverlay() {
  const section = useFlowStore(s => s.selectedSection);
  if (!section) return null;
  const z1 = beatToZ(section.startBeat, 0);
  const z2 = beatToZ(section.endBeat, 0);
  const len = Math.max(0.05, Math.abs(z2 - z1));
  const centerZ = (z1 + z2) / 2;
  return (
    <>
      <mesh position={[0, FLOOR_Y + 0.025, centerZ]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
        <planeGeometry args={[CUR_DECK_W, len]} />
        <meshBasicMaterial color="#FFFFFF" transparent opacity={0.12} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      {[z1, z2].map((z, i) => (
        <mesh key={i} position={[0, FLOOR_Y + 0.035, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
          <planeGeometry args={[CUR_DECK_W, 0.08]} />
          <meshBasicMaterial color="#FFFFFF" transparent opacity={0.8} depthWrite={false} />
        </mesh>
      ))}
    </>
  );
}

function ComplexityFloorOverlay() {
  const chart = useFlowStore(s => s.chart);
  const activeDifficulty = useFlowStore(s => s.activeDifficulty);
  const showComplexity = useFlowStore(s => s.showComplexity);

  const flags = useMemo(() => {
    if (!showComplexity) return [];
    return analyzeFlowDifficulty(chart.notes, chart.bpm, activeDifficulty).flags;
  }, [showComplexity, chart.notes, chart.bpm, activeDifficulty]);

  if (flags.length === 0) return null;
  const y = FLOOR_Y + 0.015; // just above the floor grid, below selection (0.025)

  return (
    <>
      {flags.map((f, i) => {
        const z1 = beatToZ(f.startBeat, 0);
        const z2 = beatToZ(Math.max(f.endBeat, f.startBeat + 0.25), 0);
        const centerZ = (z1 + z2) / 2;
        const len = Math.max(0.1, z2 - z1);
        const over = f.severity === 'over';
        return (
          <mesh
            key={i}
            position={[0, y, centerZ]}
            rotation={[-Math.PI / 2, 0, 0]}
            raycast={() => null}
          >
            <planeGeometry args={[CUR_DECK_W - 0.1, len]} />
            <meshBasicMaterial
              color={over ? '#EF4444' : '#EAB308'}
              transparent
              opacity={over ? 0.22 : 0.16}
              depthWrite={false}
            />
          </mesh>
        );
      })}
      {/* thin bright edge lines at each band's near/far boundary for visibility */}
      {flags.map((f, i) => {
        const z1 = beatToZ(f.startBeat, 0);
        const over = f.severity === 'over';
        return (
          <mesh key={`edge-${i}`} position={[0, y + 0.002, z1]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
            <planeGeometry args={[CUR_DECK_W - 0.1, 0.08]} />
            <meshBasicMaterial color={over ? '#EF4444' : '#EAB308'} transparent opacity={0.9} depthWrite={false} />
          </mesh>
        );
      })}
    </>
  );
}

// ── FBX note mesh ────────────────────────────────────────────────────────────

const NOTE_FBX_PATH = '/models/Note.fbx';
// Eagerly cache so the first visible note doesn't stall
useFBX.preload(NOTE_FBX_PATH);

/**
 * ONE normalised note geometry, built from Note.fbx the first time it's needed
 * and shared by every note on screen.
 *
 * This used to be done per note instance (clone the FBX hierarchy → clone each
 * child geometry → computeVertexNormals → new material). With ~90 notes in the
 * window that meant ~90 geometry copies plus, because the FBX ships morph
 * attributes, ~90 morph DataArrayTextures uploaded to the GPU as notes scrolled
 * in — the frame-time spikes users saw on dense charts. Now it's:
 *   • child transforms baked in, sub-meshes merged → ONE draw call per note
 *   • position only (morph/UV/skin data dropped), smooth normals recomputed once
 *   • centred on the origin and scaled so the longest axis is NOTE_LENGTH·0.95,
 *     so per-note size is just a group scale.
 * (Same recipe the in-browser player uses — see PlayGame.buildNoteGeometry.)
 */
const noteGeoCache = new WeakMap<THREE.Object3D, THREE.BufferGeometry>();

function sharedNoteGeometry(fbx: THREE.Group): THREE.BufferGeometry {
  const cached = noteGeoCache.get(fbx);
  if (cached) return cached;
  fbx.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  fbx.traverse(child => {
    const m = child as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    let g = m.geometry.clone();
    g.applyMatrix4(m.matrixWorld);
    if (g.index) g = g.toNonIndexed();
    const pos = g.getAttribute('position') as THREE.BufferAttribute;
    const only = new THREE.BufferGeometry();     // position only → no morphs
    only.setAttribute('position', pos.clone());
    parts.push(only);
    g.dispose();
  });
  const merged = (parts.length === 1 ? parts[0] : mergeBufferGeometries(parts, false)) || parts[0];
  // The FBX bakes hard-edge split normals (smooth groups) that show up as
  // diagonal crease artifacts under a PBR material — re-derive them smooth.
  merged.computeVertexNormals();
  merged.computeBoundingBox();
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  merged.boundingBox!.getSize(size);
  merged.boundingBox!.getCenter(center);
  merged.translate(-center.x, -center.y, -center.z);
  const maxDim = Math.max(size.x, size.y, size.z, 0.001);
  const s = (NOTE_LENGTH * 0.95) / maxDim;
  merged.scale(s, s, s);
  merged.computeBoundingSphere();
  noteGeoCache.set(fbx, merged);
  return merged;
}

/** Note materials, shared by look. Notes only differ by lane colour + the
 *  Gameplay settings, so a handful of materials covers the whole chart
 *  (one per note used to mean ~90 materials rebuilt on every settings tweak). */
const noteMatCache = new Map<string, THREE.MeshStandardMaterial>();

function sharedNoteMaterial(
  color: string, emissiveIntensity: number, metalness: number,
  roughness: number, transparent: boolean, opacity: number,
): THREE.MeshStandardMaterial {
  const t = transparent || opacity < 1;
  const key = [color, emissiveIntensity.toFixed(2), metalness.toFixed(2),
    roughness.toFixed(2), t ? 1 : 0, opacity.toFixed(2)].join('|');
  const hit = noteMatCache.get(key);
  if (hit) return hit;
  const mat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity, roughness, metalness,
    transparent: t, opacity, depthWrite: !t,
  });
  // Dragging a settings slider mints a material per step — keep the map small.
  if (noteMatCache.size > 64) {
    const oldest = noteMatCache.keys().next().value as string | undefined;
    if (oldest) { noteMatCache.get(oldest)?.dispose(); noteMatCache.delete(oldest); }
  }
  noteMatCache.set(key, mat);
  return mat;
}

/**
 * Renders the note model with the lane colour applied. Geometry and material
 * are shared (see above) — an instance is just a scaled mesh, so notes can
 * scroll in and out without allocating anything.
 *
 * Wrapped in a Suspense boundary at the call-site so other scene objects
 * paint while the first FBX load resolves (should be instant after preload).
 */
export function NoteModel({
  color,
  emissiveIntensity = 0.35,
  metalness = 0.22,
  roughness = 0.28,
  noteScale = 1.0,
  transparent = false,
  opacity = 1,
}: {
  color: string;
  emissiveIntensity?: number;
  metalness?: number;
  roughness?: number;
  noteScale?: number;
  transparent?: boolean;
  opacity?: number;
}) {
  const fbx = useFBX(NOTE_FBX_PATH);
  const geometry = useMemo(() => sharedNoteGeometry(fbx), [fbx]);
  const material = useMemo(
    () => sharedNoteMaterial(color, emissiveIntensity, metalness, roughness, transparent, opacity),
    [color, emissiveIntensity, metalness, roughness, transparent, opacity],
  );
  return <mesh geometry={geometry} material={material} scale={noteScale} />;
}

// ── Thick trail ribbon (flat plane) ─────────────────────────────────────────
// Used when trailWidth > TUBE_THRESHOLD.
// Builds a flat quad-strip BufferGeometry that follows the Catmull-Rom spline.
// At each sample the perpendicular direction in the horizontal XZ plane is
// computed so the ribbon always lies flat on the floor — Magic Tiles style.
// Geometry is memoized per note + trailWidth + laneColors.

function TrailTube({
  note,
  laneColors,
  trailWidth,
  onRemove,
}: {
  note: FlowNote;
  laneColors: readonly string[];
  /** Full ribbon width in world units (not a radius). */
  trailWidth: number;
  onRemove: () => void;
}) {
  const meshRef  = useRef<THREE.Mesh | null>(null);
  const geomRef  = useRef<THREE.BufferGeometry | null>(null);
  // Rebuild the ribbon when NJS changes (beatToZ spacing depends on it).
  const njs = useFlowStore(s => s.flowNjs);

  // ── Imperative geometry rebuild ───────────────────────────────────────────
  // Using useEffect + direct mesh.geometry assignment instead of useMemo +
  // geometry prop, so Alt+drag Zustand mutations (which bypass React renders)
  // still trigger a geometry rebuild via note object identity change.
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const wps = note.waypoints && note.waypoints.length >= 2
      ? note.waypoints
      : (note.duration_beats && note.duration_beats > 0
          ? [
              { lane: note.lane, time_beats: note.time_beats },
              { lane: note.lane, time_beats: note.time_beats + note.duration_beats },
            ] as FlowTrailWaypoint[]
          : null);

    if (!wps) { mesh.visible = false; return; }

    const ctrl = wps.map(w => new THREE.Vector3(
      laneCenterX(w.lane),
      FLOOR_Y,
      beatToZ(w.time_beats, 0),
    ));
    const curve = new THREE.CatmullRomCurve3(ctrl, false, 'centripetal');
    const samples = Math.max(64, (wps.length - 1) * 32);
    const halfW = trailWidth / 2;

    // Just above the floor grid lines (grid lines peak at y ≈ 0.022).
    const RIBBON_Y = FLOOR_Y + 0.025;

    // Per-waypoint colours for the horizontal gradient.
    const wpColors = wps.map(
      w => new THREE.Color(laneColors[w.lane] ?? FLOW_LANE_COLORS[w.lane]),
    );

    const positions: number[] = [];
    const normals:   number[] = [];
    const colorAttr: number[] = [];
    const uvs:       number[] = [];
    const indices:   number[] = [];

    // No end trimming — ribbon runs full length; note heads cover the ends.
    for (let i = 0; i <= samples; i++) {
      const u  = i / samples;
      const pt = curve.getPointAt(u);
      const tg = curve.getTangentAt(u);

      const rx = -tg.z, rz = tg.x;
      const rLen = Math.sqrt(rx * rx + rz * rz);
      const nrx = rLen > 1e-4 ? rx / rLen : 1;
      const nrz = rLen > 1e-4 ? rz / rLen : 0;

      const lx = pt.x - nrx * halfW, lz = pt.z - nrz * halfW;
      const vx = pt.x + nrx * halfW, vz = pt.z + nrz * halfW;

      const segF   = u * (wps.length - 1);
      const segIdx = Math.min(Math.floor(segF), wps.length - 2);
      const c = wpColors[segIdx].clone().lerp(wpColors[segIdx + 1], segF - segIdx);

      positions.push(lx, RIBBON_Y, lz,  vx, RIBBON_Y, vz);
      normals.push(0, 1, 0,  0, 1, 0);
      colorAttr.push(c.r, c.g, c.b,  c.r, c.g, c.b);
      uvs.push(0, u,  1, u);
    }

    for (let i = 0; i < samples; i++) {
      const a = i * 2, b = i * 2 + 1, c2 = i * 2 + 2, d = i * 2 + 3;
      indices.push(a, b, c2);
      indices.push(b, d, c2);
    }

    const newGeom = new THREE.BufferGeometry();
    newGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    newGeom.setAttribute('normal',   new THREE.Float32BufferAttribute(normals,   3));
    newGeom.setAttribute('color',    new THREE.Float32BufferAttribute(colorAttr, 3));
    newGeom.setAttribute('uv',       new THREE.Float32BufferAttribute(uvs,       2));
    newGeom.setIndex(indices);

    // Dispose the previous geometry and swap in the new one imperatively.
    geomRef.current?.dispose();
    geomRef.current = newGeom;
    mesh.geometry   = newGeom;
    mesh.visible    = true;
  }, [note, trailWidth, laneColors, njs]);

  // Dispose geometry on unmount.
  useEffect(() => () => { geomRef.current?.dispose(); geomRef.current = null; }, []);

  // ── Animated sweep shader ─────────────────────────────────────────────────
  const shaderMat = useMemo(() => new THREE.ShaderMaterial({
    vertexColors: true,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    uniforms: {
      uTime:         { value: 0 },
      uPlaying:      { value: 0 },
      uBeatProgress: { value: -1 },
      // 0 = static ribbon (no moving highlight). Driven per frame: the sweep
      // only runs within a few beats of the editor plane, and the Settings
      // toggle can kill it outright (reviewer ask — far-away rail VFX was
      // burning GPU on weak machines).
      uActive:       { value: 1 },
    },
    vertexShader: /* glsl */`
      varying vec3  vColor;
      varying vec2  vUv;
      void main() {
        vColor = color;
        vUv    = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */`
      uniform float uTime;
      uniform float uPlaying;
      uniform float uBeatProgress;
      uniform float uActive;
      varying vec3  vColor;
      varying vec2  vUv;

      void main() {
        // UV.x in [0,1] across width, UV.y in [0,1] along length.
        float normY = vUv.y;   // [0,1] along ribbon

        // Soft edge fade on the width axis only — note heads cover the ends.
        float du       = vUv.x * 2.0 - 1.0;            // [-1,1] across width
        float edgeFade = 1.0 - smoothstep(0.80, 1.0, abs(du));

        // ── Moving highlight ─────────────────────────────────────────────
        // uActive gates the sweep: far from the editor plane (or with the
        // "Rail Sweep VFX" toggle off) the ribbon renders flat.
        float posN;
        if (uActive < 0.5) {
          posN = -99.0;
        } else if (uPlaying > 0.5) {
          posN = uBeatProgress;
          if (posN < 0.0 || posN > 1.0) posN = -99.0;
        } else {
          posN = mod(uTime * 0.40, 1.0);
        }

        float d    = abs(normY - posN);
        d = min(d, 1.0 - d);

        float band = exp(-d * d * 55.0);
        float core = exp(-d * d * 380.0);
        float oval = exp(-du * du * 1.8);

        vec3 col = vColor * 0.72;
        col += vColor * band  * 0.55 * oval;
        col += vec3(1.0) * core * 0.90 * oval;

        gl_FragColor = vec4(col, 0.88 * edgeFade);
      }
    `,
  }), []);

  useFrame(({ clock }) => {
    const state = useFlowStore.getState();
    shaderMat.uniforms.uTime.value    = clock.elapsedTime;
    shaderMat.uniforms.uPlaying.value = state.playing ? 1.0 : 0.0;
    // Sweep gate (reviewer perf ask): animate only within ~3 beats of the
    // editor plane, and not at all when the toggle is off. Far ribbons render
    // flat — same colours, zero per-frame shimmer cost on the eye and less
    // blending work on fill-bound GPUs.
    const dur = note.duration_beats > 0 ? note.duration_beats : 1;
    const end = note.time_beats + dur;
    const distBeats = state.currentBeat < note.time_beats
      ? note.time_beats - state.currentBeat
      : state.currentBeat > end ? state.currentBeat - end : 0;
    const vfxOn = state.gameplaySettings.trailSweepVfx !== false
      && ACTIVE_FX.railSweep             // preset gate: flat ribbons, no shimmer
      && distBeats <= 3;
    shaderMat.uniforms.uActive.value = vfxOn ? 1.0 : 0.0;
    if (state.playing) {
      shaderMat.uniforms.uBeatProgress.value =
        (state.currentBeat - note.time_beats) / dur;
    }
  });

  useEffect(() => () => { shaderMat.dispose(); }, [shaderMat]);

  // Start hidden; the geometry useEffect above will make it visible once built.
  return (
    <mesh
      ref={meshRef}
      visible={false}
      material={shaderMat}
      onClick={(e: ThreeEvent<MouseEvent>) => { e.stopPropagation(); onRemove(); }}
    />
  );
}

// ── Song waveform along the runway ───────────────────────────────────────────
//
// A static, non-interactive bar visualisation of the track's amplitude envelope,
// drawn down BOTH outer edges of the highway so you can see the song's shape
// along the timeline. Mounted inside worldRef, so it scrolls with the notes
// (each bar sits at its beat's absolute z; the group translation does the rest).

// Song waveform as a baked SPRITE: instead of thousands of 3D bar instances
// (slow to build) the amplitude "palitos" are drawn ONCE to an offscreen canvas
// and shown as a single textured plane down each runway edge. One texture + two
// planes ⇒ instant load, scrolls with the world group in perspective.

const SPRITE_TEX_W = 256;     // px across (amplitude axis) — higher = crisper

function WaveformSprite() {
  const duration = useFlowStore(s => s.duration);
  const bpm = useFlowStore(s => s.chart.bpm);
  const njs = useFlowStore(s => s.flowNjs);
  const { gl } = useThree();

  // Colour / opacity / density come from the Interface settings (UI tab).
  const iface = useFlowStore(s => s.flowInterfaceSettings);
  // The waveform bars follow the TINT theme (background tone), resolved to hex
  // for the canvas texture; re-bakes when the tint changes (barColor dep below).
  const themeTint = useTheme(s => s.tint);
  const themeTintHex = useTheme(s => s.tintHex);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const barColor = useMemo(() => tintHex(0.55, 0.11), [themeTint, themeTintHex]);
  const opacity = iface.waveformOpacity ?? 0.9;
  const buckets = Math.max(200, Math.round(iface.waveformDensity || 4000));

  // Create an EMPTY canvas/texture up front (shown immediately), then a separate
  // effect draws the palitos batch-by-batch starting at beat 0 — so the waveform
  // appears right away and grows from the start instead of blocking on a full bake.
  // The audio buffer may still be decoding when `duration` is first set, so poll
  // getPeaks until it's ready before building (otherwise the sprite never shows).
  const [built, setBuilt] = useState<{
    ctx: CanvasRenderingContext2D; tex: THREE.CanvasTexture;
    peaks: Float32Array; wpx: number; hpx: number; rowH: number;
    buckets: number; color: string; totalBeats: number;
  } | null>(null);
  useEffect(() => {
    if (duration <= 0 || typeof document === 'undefined') { setBuilt(null); return; }
    let cancelled = false;
    let tries = 0;
    let timer = 0;
    const build = () => {
      if (cancelled) return;
      // Give every bar a fixed 4px row (2px bar + 2px gap) so it always renders
      // crisp with a visible gap; cap the bar count to whatever the GPU's max
      // texture size allows at that resolution (beyond it, more bars would be
      // sub-pixel → blurry and the gap would vanish).
      const maxTex = gl.capabilities?.maxTextureSize || 8192;
      // Each bar needs a 4px row (2px bar + 2px gap) to keep a VISIBLE gap under
      // perspective minification — thinner rows merge into a solid band. So the
      // effective bar count is capped at maxTex/4 (beyond that, density can't add
      // more bars without losing the separation).
      const ROW_PX = 4;
      const n = Math.min(buckets, Math.floor(maxTex / ROW_PX));
      const peaks = getPeaks(n);
      if (!peaks) {                       // audio not decoded yet — retry shortly
        if (tries++ < 60) timer = window.setTimeout(build, 150);
        return;
      }
      const wpx = SPRITE_TEX_W;
      const hpx = n * ROW_PX;
      const canvas = document.createElement('canvas');
      canvas.width = wpx;
      canvas.height = hpx;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, wpx, hpx);
      const tex = new THREE.CanvasTexture(canvas);
      // The strip lies almost edge-on to the camera, so the texture is heavily
      // compressed along its length. Plain mip filtering over-blurs that and
      // bleeds colour into the gaps; ANISOTROPIC filtering samples along the
      // compressed axis → crisp lines AND fully-transparent gaps (no bleed).
      tex.magFilter = THREE.NearestFilter;        // crisp bar edges up close (no soft interpolation)
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.generateMipmaps = true;
      tex.anisotropy = gl.capabilities.getMaxAnisotropy?.() || 1;
      tex.needsUpdate = true;
      setBuilt({
        ctx, tex, peaks, wpx, hpx, rowH: ROW_PX, buckets: n, color: barColor,
        totalBeats: Math.max(1, (duration * bpm) / 60),
      });
    };
    build();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // Rebuild when the track, density or colour changes (opacity is applied live).
  }, [duration, bpm, gl, buckets, barColor]);

  // Intro animation: each bar grows from 0 → its full size, staggered from beat
  // 0 outward (a sweep), the first time the texture is built. The whole texture
  // is redrawn each frame for the short intro, then it settles to a static image.
  // (Texture is flipY → canvas top = near/strike, so beat 0 grows first.)
  useEffect(() => {
    if (!built) return;
    const { ctx, tex, peaks, wpx, hpx, rowH, buckets: n, color, totalBeats } = built;
    const maxBarW = wpx - 2;
    // The wave sweeps up the runway by BEAT position (not bucket index), so the
    // ~16 visible beats actually stagger over the sweep instead of all at once.
    // Bars beyond the visible depth finish by the end (they sit at the far apex).
    const SWEEP_BEATS = 20;      // how many beats of runway the wave travels across
    const SWEEP_SPAN = 4500;     // ms for the wave to cross that depth (3× slower)
    const GROW = 1100;           // ms each bar takes to grow 0 → full (3× slower)
    const beatPerBucket = totalBeats / n;
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
    let raf = 0;
    let start = 0;

    const render = (elapsed: number | null) => {       // null = final full pass
      ctx.clearRect(0, 0, wpx, hpx);
      ctx.fillStyle = color;
      for (let i = 0; i < n; i++) {
        let g = 1;
        if (elapsed !== null) {
          const beat = i * beatPerBucket;                          // 0 at strike → up the runway
          const appear = Math.min(1, beat / SWEEP_BEATS) * SWEEP_SPAN;
          const p = (elapsed - appear) / GROW;
          if (p <= 0) continue;                          // not appeared yet
          g = easeOut(Math.min(1, p));
        }
        const barW = Math.max(0.5, peaks[i] * maxBarW * g);
        const y = i * rowH;
        ctx.fillRect(0, y + rowH * 0.25, barW, Math.max(1, rowH * 0.5));
      }
      tex.needsUpdate = true;
    };

    const step = (now: number) => {
      if (!start) start = now;
      const elapsed = now - start;
      render(elapsed);
      if (elapsed < SWEEP_SPAN + GROW) raf = requestAnimationFrame(step);
      else render(null);                                 // settle to final crisp image
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [built]);

  useEffect(() => () => built?.tex.dispose(), [built]);
  const texture = built?.tex ?? null;

  const geo = useMemo(() => {
    const totalBeats = Math.max(1, (duration * bpm) / 60);
    const z0 = beatToZ(0, 0);
    const z1 = beatToZ(totalBeats, 0);
    return { centerZ: (z0 + z1) / 2, length: Math.max(1, Math.abs(z1 - z0)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, bpm, njs]);

  if (!texture || duration <= 0 || opacity <= 0 || iface.waveformVisible === false) return null;
  const W = Math.max(0.2, iface.waveformHeight ?? 2.2);          // outward reach = visual height
  const edge = CUR_DECK_W / 2 + (iface.waveformGap ?? 0.3);      // distance from the runway edge
  return (
    <>
      {[1, -1].map((sign) => (
        <mesh
          key={sign}
          position={[sign * (edge + W / 2), FLOOR_Y + 0.02, geo.centerZ]}
          rotation={[-Math.PI / 2, 0, 0]}
          scale={[sign, 1, 1]}            // mirror X on the left so palitos point outward
          raycast={() => null}
        >
          <planeGeometry args={[W, geo.length]} />
          <meshBasicMaterial map={texture} transparent opacity={opacity} depthWrite={false} />
        </mesh>
      ))}
    </>
  );
}

// ── Note placement "drop + rubber bounce" entrance ───────────────────────────
//
// When a note is added to the chart we stamp its id with the time. NoteEntrance
// (the wrapper around each note group) animates that note for ~0.7s: it drops
// from above and settles with a damped jello wobble (squash/stretch), like a
// little rubber note landing on the deck. Stamps clear once the anim finishes.
const noteSpawns = new Map<string, number>();

/** Record that the note nearest (lane, beat) was just placed, so it animates in.
 *  No-op when note animations are turned off in Settings. */
function markNoteSpawned(lane: number, beat: number): void {
  const st = useFlowStore.getState();
  if (st.flowInterfaceSettings?.noteAnimations === false) return;
  const notes = st.chart.notes;
  let best: FlowNote | undefined;
  let bestD = Infinity;
  for (const n of notes) {
    if (n.lane !== lane) continue;
    const d = Math.abs(n.time_beats - beat);
    if (d < bestD) { bestD = d; best = n; }
  }
  if (best && bestD < 0.25) noteSpawns.set(best.id, performance.now());
}

/** Stamp the CLOSING (tail) note of a just-committed trail so it plays the same
 *  drop/bounce entrance as a freshly-placed note. Keyed by `${id}#tail` to match
 *  the tail's NoteEntrance. No-op when note animations are off or the note isn't
 *  a real (≥2-waypoint) trail. Call right AFTER commitTrail(), passing the id
 *  that was pending before the commit. */
function markTrailTailSpawned(noteId: string | null): void {
  if (!noteId) return;
  const st = useFlowStore.getState();
  if (st.flowInterfaceSettings?.noteAnimations === false) return;
  const n = st.chart.notes.find(x => x.id === noteId);
  if (n && n.waypoints && n.waypoints.length >= 2) {
    noteSpawns.set(`${noteId}#tail`, performance.now());
  }
}

function NoteEntrance({ noteId, position, onClick, onPointerDown, children }: {
  noteId: string;
  position: [number, number, number];
  onClick: (e: ThreeEvent<MouseEvent>) => void;
  onPointerDown: (e: ThreeEvent<PointerEvent>) => void;
  children: React.ReactNode;
}) {
  const g = useRef<THREE.Group>(null);
  useFrame(() => {
    const grp = g.current;
    if (!grp) return;
    // Play Test: a note you HIT disappears; a missed one keeps flying past.
    const hidden = PLAY_TEST_ON && playTestHitIds.has(noteId);
    if (grp.visible === hidden) grp.visible = !hidden;
    const spawn = noteSpawns.get(noteId);
    if (spawn === undefined) return;                 // not animating → leave as declared
    const e = (performance.now() - spawn) / 1000;
    const DROP_H = 1.4, DROP_DUR = 0.08, BOUNCE_DUR = 0.5;
    const DUR = DROP_DUR + BOUNCE_DUR;
    if (e >= DUR) {                                  // settle to the clean transform
      grp.position.y = position[1];
      grp.scale.set(1, 1, 1);
      noteSpawns.delete(noteId);
      return;
    }
    if (e < DROP_DUR) {
      // Accelerating fall (gravity-ish). No squash while in the air.
      const t = e / DROP_DUR;
      grp.position.y = position[1] + DROP_H * (1 - t * t);
      grp.scale.set(1, 1, 1);
    } else {
      // The instant it lands → the rubber jiggle starts (x/z bulge, y squash).
      const b = e - DROP_DUR;
      const w = Math.sin(b * 26) * Math.exp(-b * 7) * 0.34;
      grp.position.y = position[1];
      grp.scale.set(1 + w, Math.max(0.25, 1 - w), 1 + w);
    }
  });
  return (
    <group ref={g} position={position} onClick={onClick} onPointerDown={onPointerDown}>
      {children}
    </group>
  );
}

// ── Note delete "shatter" burst ──────────────────────────────────────────────
//
// Deleting a note spawns a pool of small cube shards at its spot (in beat-space,
// so they scroll with the world). Each shard flies outward + up, tumbles, falls
// under gravity. Some shards are "bouncy" and ricochet on the timeline floor
// for a couple of seconds; the rest are a quick explosion. Each shard fades over
// its own life. ShatterField draws them all in one InstancedMesh (per-colour).
const MAX_SHARDS = 420;

interface Shard {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  rx: number; ry: number; rz: number;
  rvx: number; rvy: number; rvz: number;
  size: number; born: number; color: string;
  life: number;        // seconds this shard lives
  rest: number;        // floor restitution (bounciness)
}
const shatterShards: Shard[] = [];

function spawnShatter(x: number, y: number, z: number, color: string, count = 24): void {
  const now = performance.now();
  for (let i = 0; i < count && shatterShards.length < MAX_SHARDS; i++) {
    const a = Math.random() * Math.PI * 2;
    const out = 1.6 + Math.random() * 3.6;             // 2× outward spread
    // ~40% are "bouncy": they keep a low arc and ricochet on the timeline floor
    // for a couple of seconds; the rest are the quick explosion shards.
    const bouncy = Math.random() < 0.4;
    shatterShards.push({
      x: x + (Math.random() - 0.5) * 0.18, y, z: z + (Math.random() - 0.5) * 0.18,
      vx: Math.cos(a) * out, vz: Math.sin(a) * out * 0.6,
      vy: bouncy ? 1 + Math.random() * 2 : 3 + Math.random() * 5,
      rx: Math.random() * 6, ry: Math.random() * 6, rz: Math.random() * 6,
      rvx: (Math.random() - 0.5) * 26, rvy: (Math.random() - 0.5) * 26, rvz: (Math.random() - 0.5) * 26,
      size: 0.05 + Math.random() * 0.07, born: now, color,
      life: bouncy ? 2.0 + Math.random() * 1.3 : 0.5 + Math.random() * 0.25,
      rest: bouncy ? 0.55 : 0.3,
    });
  }
}

/** Burst a note into shards at its 3D spot, using the note's CONFIGURED lane
 *  colour (gameplaySettings.laneColors) so the effect tracks any colour the
 *  user sets in Settings; falls back to the built-in palette. */
function shatterNote(state: any, note: FlowNote, silent = false): void {
  // The shatter shards are gated by BOTH the note-animations toggle and the
  // dedicated Delete VFX toggle (users clearing many notes at once found the
  // burst noisy); the delete sound is independent (it has its own "Off"
  // option). `silent` skips the sound for a mirror twin so we don't double
  // the delete blip.
  if (state?.flowInterfaceSettings?.noteAnimations !== false
      && state?.flowInterfaceSettings?.deleteVfx !== false) {
    const lc = state?.gameplaySettings?.laneColors?.[note.lane] ?? FLOW_LANE_COLORS[note.lane];
    const yh = state?.gameplaySettings?.noteHeight ?? 0;
    spawnShatter(laneCenterX(note.lane), FLOOR_Y + NOTE_HEIGHT / 2 + yh, beatToZ(note.time_beats, 0), lc);
  }
  if (!silent) playDeleteSound();
}

/** When Mirror mode is on, the id list to delete for a note = the note + its
 *  mirror twin (same beat on the FLOW lane mirrored across the centre). Returns
 *  just [noteId] in Normal mode or when no twin exists. */
function withMirror(noteId: string): string[] {
  const st = useFlowStore.getState();
  if (!st.noteMirror) return [noteId];
  const note = st.chart.notes.find(n => n.id === noteId);
  if (!note) return [noteId];
  const lc = laneCountFor(st.chart, st.activeDifficulty);
  const ml = lc - 1 - note.lane;
  const twin = st.chart.notes.find(n =>
    n.id !== noteId && n.lane === ml && Math.abs(n.time_beats - note.time_beats) < 0.02,
  );
  return twin ? [noteId, twin.id] : [noteId];
}

function ShatterField() {
  const ref = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const tmpColor = useMemo(() => new THREE.Color(), []);
  useFrame((_s, dt) => {
    const m = ref.current;
    if (!m) return;
    const now = performance.now();
    const d = Math.min(dt, 0.05);
    for (let i = shatterShards.length - 1; i >= 0; i--) {
      const s = shatterShards[i];
      if ((now - s.born) / 1000 >= s.life) { shatterShards.splice(i, 1); continue; }
      s.vy -= 9 * d;                                   // gravity
      s.x += s.vx * d; s.y += s.vy * d; s.z += s.vz * d;
      s.rx += s.rvx * d; s.ry += s.rvy * d; s.rz += s.rvz * d;
      // Only the runway deck is a collider: shards still over the track
      // (|x| within the lanes) bounce; ones that flew off the sides keep
      // falling into the void instead of hovering on the invisible floor plane.
      if (s.y < FLOOR_Y && Math.abs(s.x) <= CUR_DECK_W / 2) {
        s.y = FLOOR_Y;
        s.vy *= -s.rest;
        s.vx *= 0.78; s.vz *= 0.78;                    // floor friction → skitter & settle
        s.rvx *= 0.7; s.rvy *= 0.7; s.rvz *= 0.7;
      }
    }
    const n = shatterShards.length;
    for (let i = 0; i < n; i++) {
      const s = shatterShards[i];
      const age = (now - s.born) / 1000;
      const fadeStart = s.life * 0.7;                  // full size, then shrink over the last 30%
      const k = age < fadeStart ? 1 : Math.max(0.05, 1 - (age - fadeStart) / (s.life - fadeStart));
      dummy.position.set(s.x, s.y, s.z);
      dummy.rotation.set(s.rx, s.ry, s.rz);
      dummy.scale.setScalar(s.size * k);
      dummy.updateMatrix();
      m.setMatrixAt(i, dummy.matrix);
      tmpColor.set(s.color);
      m.setColorAt(i, tmpColor);
    }
    m.count = n;
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
  });
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, MAX_SHARDS]} frustumCulled={false} raycast={() => null}>
      <boxGeometry args={[1, 1, 1]} />
      <meshBasicMaterial toneMapped={false} />
    </instancedMesh>
  );
}

// ── Inner scene contents ─────────────────────────────────────────────────────

function SceneContents({
  onPlace,
  onAltDragStart,
  onDragArm,
  altDragJustEndedRef,
  r3fHitRef,
}: {
  onPlace: (lane: FlowLaneIndex, beat: number, shiftKey?: boolean) => void;
  /** Triggered by Alt+pointerdown on a note or waypoint. The wrapper then
   *  takes over with window-level pointermove/pointerup to do the drag. */
  onAltDragStart: (noteId: string, waypointIndex: number | null) => void;
  /** ARM a deferred drag (trail waypoint/tail): it only activates after real
   *  pointer movement, so a no-move press stays a click. */
  onDragArm: (noteId: string, waypointIndex: number | null, x: number, y: number) => void;
  /** Set true for one rAF after an alt-drag ends so handleFloorClick can
   *  ignore the spurious R3F onClick that fires on release. */
  altDragJustEndedRef: React.MutableRefObject<boolean>;
  /** Set true when a click landed on an R3F object (a note), so the
   *  canvas-level empty-space deselect doesn't also clear on note clicks. */
  r3fHitRef: React.MutableRefObject<boolean>;
}) {
  const chart = useFlowStore(s => s.chart);
  const selectedIds = useFlowStore(s => s.selection.noteIds);
  const cutNoteIds = useFlowStore(s => s.cutNoteIds);
  const removeNotes = useFlowStore(s => s.removeNotes);
  const pendingTrailNoteId = useFlowStore(s => s.pendingTrailNoteId);
  // Active perf preset (user-editable): drives the frame cap + the "skip the
  // expensive extras" flags below (waveform, per-beat chips).
  const PRESET_FX = useEditorStore(s => s.performanceSettings[s.perfMode].flowFx);
  const PRESET_FPS_CAP = useEditorStore(s => s.performanceSettings[s.perfMode].fpsCap);
  // The benchmark measures the editor's real uncapped rate — the cap unmounts
  // while it runs (a 30fps cap would floor every reading at 30).
  const BENCHMARKING = useEditorStore(s => s.benchmarkOpen);
  // NJS (editor note-spacing multiplier). Set the module scale synchronously
  // here so every beatToZ() call in this render (and descendants) uses it.
  const flowNjs = useFlowStore(s => s.flowNjs);
  NJS_SCALE = flowNjs;
  // Active difficulty's lane count (3, 4 or 5) → drives lane distribution and
  // (past 4 lanes) how wide the deck itself is.
  const laneCount = useFlowStore(s => laneCountFor(s.chart, s.activeDifficulty));
  CUR_LANE_COUNT = laneCount;
  const deckW = deckWidthFor(laneCount);
  CUR_DECK_W = deckW;
  // Play Test run — hides the snap grid and gates the automatic hit visuals.
  const playTest = useFlowStore(s => s.playTest);
  // Mirror placement → the hover marker also previews the mirrored lane.
  const noteMirror = useFlowStore(s => s.noteMirror);

  // Visual settings — read once per render so changes propagate immediately.
  const gameplaySettings  = useFlowStore(s => s.gameplaySettings);
  const ifaceSettings     = useFlowStore(s => s.flowInterfaceSettings);
  const laneColors = gameplaySettings.laneColors;
  // During playback the note heads are hidden — only the trails scroll by,
  // giving a clean gameplay view. Subscribing here triggers a single
  // re-render on play/pause toggle (not every frame).
  const playing = useFlowStore(s => s.playing);
  const duration = useFlowStore(s => s.duration);

  // Runway floor length: cover the WHOLE song (+ a lookahead margin) so the
  // deck, lane dividers and grid never run out from under the notes on long
  // charts. The old fixed length capped at ~320 beats, so anything past beat
  // 320 had no floor and the 3D timeline "disappeared". Falls back to that
  // minimum for short/empty charts.
  const runwayLength = useMemo(() => {
    const unit = unitPerBeat();
    const totalBeats = (duration * chart.bpm) / 60;
    const beats = Math.max(VISIBLE_BEATS_AHEAD * 20, totalBeats + VISIBLE_BEATS_AHEAD + 16);
    return beats * unit;
  }, [duration, chart.bpm, flowNjs]);

  const worldRef = useRef<THREE.Group>(null);

  // ── Trail closing-ring preview ─────────────────────────────────────────────
  // While a trail is in progress, holding Shift shows a pill "ring" under the
  // cursor marking exactly where the trail will close. Shift+click on it drops
  // the final note and finishes the trail; then a fresh click starts the next.
  const [trailClose, setTrailClose] = useState<{ lane: FlowLaneIndex; beat: number } | null>(null);
  const lastHoverRef = useRef<{ lane: FlowLaneIndex; beat: number } | null>(null);
  // Placement preview: the snapped (lane, beat) cell under the cursor, shown as
  // a trigger-area ring so the user sees exactly where a click would drop a note.
  const [hoverCell, setHoverCell] = useState<{ lane: FlowLaneIndex; beat: number } | null>(null);
  // Live Shift-held state. The closing click uses this as a robust fallback for
  // e.nativeEvent.shiftKey so the FIRST click after pressing Shift closes the
  // trail (the native modifier flag on the click event can lag/miss).
  const shiftHeldRef = useRef(false);

  // Clear the ring the moment the trail is committed/cancelled.
  useEffect(() => { if (!pendingTrailNoteId) setTrailClose(null); }, [pendingTrailNoteId]);

  // Pressing/releasing Shift toggles the ring at the last-hovered cell even if
  // the pointer hasn't moved (pointermove alone wouldn't catch a key press).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Shift') return;
      shiftHeldRef.current = e.type === 'keydown';
      const s = useFlowStore.getState();
      if (e.type === 'keydown' && s.pendingTrailNoteId && lastHoverRef.current) {
        setTrailClose({ ...lastHoverRef.current });
      } else if (e.type === 'keyup') {
        setTrailClose(null);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    // Belt-and-suspenders: if focus is lost (alt-tab) we may miss the keyup, so
    // also clear on blur.
    const onBlur = () => { shiftHeldRef.current = false; };
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Floor hover → track the snapped (lane, beat) under the cursor; surface the
  // closing ring while Shift is held during a pending trail.
  const handleFloorPointerMove = useCallback((e: ThreeEvent<PointerEvent>) => {
    const state = useFlowStore.getState();
    const point = e.point;
    const x = point.x;
    const unit = unitPerBeat();
    const beat = state.currentBeat + point.z / unit;
    const subsPerBeat = state.snapDivision / 4;
    const step = 1 / subsPerBeat;
    const snappedBeat = Math.max(0, Math.round(beat / step) * step);
    const lc = laneCountFor(state.chart, state.activeDifficulty);
    const dw = deckWidthFor(lc);
    const lane = Math.min(
      lc - 1,
      Math.max(0, Math.floor((dw / 2 - x) / (dw / lc))),
    ) as FlowLaneIndex;
    // Off the runway (sides or pre-roll/offset zone) → no valid placement cell.
    // Same for a Play Test run and for cells outside the edit window: no ring,
    // so the user can see where editing is allowed before clicking.
    const outsideWindow = state.editWindowEnabled
      && Math.abs(snappedBeat - state.currentBeat) > EDIT_WINDOW_BEATS;
    if (x < -dw / 2 || x > dw / 2 || beat < 0 || state.playTest || outsideWindow) {
      lastHoverRef.current = null;
      setHoverCell(prev => (prev === null ? prev : null));
      setTrailClose(prev => (prev === null ? prev : null));
      return;
    }
    lastHoverRef.current = { lane, beat: snappedBeat };
    // Placement-preview ring follows the snapped cell under the cursor.
    setHoverCell(prev => (prev && prev.lane === lane && prev.beat === snappedBeat) ? prev : { lane, beat: snappedBeat });
    const wantRing = !!state.pendingTrailNoteId && e.nativeEvent.shiftKey;
    setTrailClose(prev => {
      if (!wantRing) return prev === null ? prev : null;
      if (prev && prev.lane === lane && prev.beat === snappedBeat) return prev;
      return { lane, beat: snappedBeat };
    });
  }, []);

  const handleFloorPointerOut = useCallback(() => {
    lastHoverRef.current = null;
    setHoverCell(prev => (prev === null ? prev : null));
    setTrailClose(prev => (prev === null ? prev : null));
  }, []);

  // Force a couple re-renders on mount to nudge r3f's first paint.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const ids: ReturnType<typeof setTimeout>[] = [];
    [60, 250, 600].forEach(d => ids.push(setTimeout(() => forceTick(x => x + 1), d)));
    return () => ids.forEach(id => clearTimeout(id));
  }, []);

  // Spark pool. Refs stay stable across renders; we read/write them in useFrame.
  const sparksRef = useRef<Spark[]>([]);
  if (sparksRef.current.length === 0) {
    sparksRef.current = Array.from({ length: MAX_SPARKS }, emptySpark);
  }
  const sparkInstRef = useRef<THREE.InstancedMesh | null>(null);

  /**
   * Geometry + material for the spark InstancedMesh.
   *
   * Instancing shares ONE material across every particle, so the old
   * `material.opacity = fade` trick can't work — each spark needs its own
   * alpha. A per-instance attribute plus a two-line shader patch gives exactly
   * that while keeping MeshBasicMaterial (and its cheap unlit path).
   *
   * Rebuilt per mount rather than kept at module scope: the Canvas remounts
   * when Potato mode toggles (antialias is a context-creation flag), and R3F
   * disposes what it created.
   */
  const { sparkGeo, sparkMat, sparkAlphaAttr } = useMemo(() => {
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const alpha = new THREE.InstancedBufferAttribute(new Float32Array(MAX_SPARKS), 1);
    alpha.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aSparkAlpha', alpha);

    const mat = new THREE.MeshBasicMaterial({
      color: '#FFFFFF', transparent: true, depthWrite: false, toneMapped: false,
    });
    mat.onBeforeCompile = (shader) => {
      shader.vertexShader = `attribute float aSparkAlpha;\nvarying float vSparkAlpha;\n${shader.vertexShader}`
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n\tvSparkAlpha = aSparkAlpha;');
      shader.fragmentShader = `varying float vSparkAlpha;\n${shader.fragmentShader}`
        .replace('#include <dithering_fragment>', '#include <dithering_fragment>\n\tgl_FragColor.a *= vSparkAlpha;');
    };
    return { sparkGeo: geo, sparkMat: mat, sparkAlphaAttr: alpha };
  }, []);

  // Track which notes have already triggered a hit this play-through.
  const lastBeatRef = useRef<number>(0);

  // Lane keypad ("pill") materials + a per-lane glow value (0..1) so we can
  // brighten a pad while its Live Mapping key is held — fast attack on press,
  // gentle release so a quick tap still flashes visibly.
  // Refs to the keypad outline Line objects (Line2); we drive their material
  // opacity/width for the held-key glow.
  const keypadMats = useRef<any[]>([]);
  const keypadGlow = useRef<number[]>(Array(FLOW_LANE_COUNT).fill(0));
  // Groups (not just the outlines) so a held key can LIFT the pad off the deck.
  const keypadGroups = useRef<(THREE.Group | null)[]>([]);
  // Per-lane hit HOP (0..1): snaps to 1 the frame a note crosses the strike
  // line, then decays exponentially — fast rise, decelerating landing
  // (reviewer ask: the outline should hop ~10px when it "takes" a note).
  const keypadHop = useRef<number[]>(Array(FLOW_LANE_COUNT).fill(0));
  /** Seconds until the next particle puff for each held trail (Play Test). */
  const holdSparkClock = useRef<number[]>(Array(FLOW_LANE_COUNT).fill(0));

  useFrame((_state, delta) => {
    const state = useFlowStore.getState();
    NJS_SCALE = state.flowNjs;   // keep the spacing scale current for beatToZ
    CUR_LANE_COUNT = laneCountFor(state.chart, state.activeDifficulty);
    CUR_DECK_W = deckWidthFor(CUR_LANE_COUNT);
    const w = worldRef.current;
    if (!w) return;
    const unit = unitPerBeat();
    w.position.z = -state.currentBeat * unit;

    PLAY_TEST_ON = state.playTest;
    {
      const es = useEditorStore.getState();
      ACTIVE_FX = es.performanceSettings[es.perfMode].flowFx;
    }

    // ── Keypad press feedback (Live Mapping + Play Test) ───────────────────
    // Held lane key → the pad brightens, thickens AND lifts off the deck, so
    // you can see which key you're on. Driven imperatively here so a keypress
    // never triggers a React render.
    {
      const base = state.flowInterfaceSettings.keypadOpacity;
      for (let i = 0; i < FLOW_LANE_COUNT; i++) {
        const ln = keypadMats.current[i];
        const grp = keypadGroups.current[i];
        const target = liveLaneHeld[i] ? 1 : 0;
        const g = keypadGlow.current[i];
        const rate = target > g ? 30 : 8;      // attack fast, release slower
        const press = g + (target - g) * Math.min(1, delta * rate);
        keypadGlow.current[i] = press;
        // Hit hop: exponential decay = fast initial fall that decelerates
        // into the landing. The rise is the snap-to-1 at the crossing below.
        const hop = keypadHop.current[i] * Math.exp(-delta * 6);
        keypadHop.current[i] = hop < 0.01 ? 0 : hop;
        if (ln && ln.material) {
          ln.material.opacity = Math.min(1, base + Math.max(press, hop) * 0.5);
          ln.material.linewidth = 2.5 + press * 1.5;   // thicken while held
        }
        // max(), not sum — a held key during a crossing shouldn't stack lifts.
        if (grp) grp.position.y = FLOOR_Y + 0.034 + Math.max(press, hop) * KEYPAD_PRESS_LIFT;
      }
    }

    // ── Hit detection: any note whose time_beats crossed currentBeat
    // since the previous frame, while playing, fires a hit + sparks.
    if (state.playing) {
      const prev = lastBeatRef.current;
      const now = state.currentBeat;
      if (now > prev) {
        const ps = useFlowStore.getState().particleSettings;
        const bpm = Math.max(1, state.chart.bpm);
        const offs = state.chart.offset_seconds;
        // Probe: a whole beat just crossed the (visual) playhead. Compare the
        // audio position now vs the beat's true audio time → A/V drift.
        const wholeBeat = Math.floor(now);
        if (wholeBeat > Math.floor(prev)) {
          const beatTime = offs + wholeBeat * 60 / bpm;
          probe('beat', {
            beat: wholeBeat,
            pos: getPosition(),
            beatTime,
            driftMs: (getPosition() - beatTime) * 1000,
          });
        }
        const lc = useFlowStore.getState().gameplaySettings.laneColors;
        const sparkAt = (lane: FlowLaneIndex) => {
          // Preset gate: no particle bursts (transparent overdraw), but the
          // pill still hops so hits stay readable.
          if (!ACTIVE_FX.sparks) { keypadHop.current[lane] = 1; return; }
          spawnSparks(sparksRef.current, laneCenterX(lane), lane, performance.now() / 1000, lc,
            sparkOpts(ps, ps.sparksPerHit));
          // The lane's trigger pill hops on the same hit the sparks mark.
          keypadHop.current[lane] = 1;
        };
        // Play Test: nothing sparks on its own — the only particles come from
        // notes the PLAYER actually hits (queued below).
        for (const note of state.playTest ? [] : state.chart.notes) {
          // Head crossing. Sound is handled by the sample-accurate look-ahead
          // scheduler in FlowApp; here we only spawn the visual hit sparks at
          // the crossing (frame timing is fine for visuals).
          if (note.time_beats > prev && note.time_beats <= now) {
            sparkAt(note.lane);
            emitFlowHit({
              xFrac: (note.lane + 0.5) / Math.max(1, CUR_LANE_COUNT),
              color: lc[note.lane] || '#ffffff',
              strong: false,
            });
          }
          // Trail CLOSING note crossing — a trail fires a second hit (sparks)
          // when its tail reaches the strike line, just like a tap note.
          const isTrail = (note.duration_beats && note.duration_beats > 0)
            || (note.waypoints && note.waypoints.length >= 2);
          if (isTrail) {
            const endBeat = note.time_beats + (note.duration_beats ?? 0);
            const endLane = (note.waypoints && note.waypoints.length >= 2)
              ? note.waypoints[note.waypoints.length - 1].lane
              : note.lane;
            if (endBeat > prev && endBeat <= now) {
              sparkAt(endLane as FlowLaneIndex);
              emitFlowHit({
                xFrac: (endLane + 0.5) / Math.max(1, CUR_LANE_COUNT),
                color: lc[endLane] || '#ffffff',
                strong: true,
              });
            }
          }
        }
      }
      lastBeatRef.current = now;
    } else {
      lastBeatRef.current = state.currentBeat;
    }

    // ── Play Test particles ────────────────────────────────────────────────
    // (a) one burst per note the player actually hits, and (b) a looping puff
    // at the strike line for as long as a hit TRAIL is being held down.
    if (state.playTest) {
      const ps = state.particleSettings;
      const lcolors = state.gameplaySettings.laneColors;
      const burst = (lane: number, strong: boolean) => {
        spawnSparks(sparksRef.current, laneCenterX(lane), lane as FlowLaneIndex,
          performance.now() / 1000, lcolors,
          sparkOpts(ps, Math.max(1, Math.round(ps.sparksPerHit * (strong ? 1 : 0.45)))));
        emitFlowHit({
          xFrac: (lane + 0.5) / Math.max(1, CUR_LANE_COUNT),
          color: lcolors[lane] || '#ffffff',
          strong,
        });
      };
      while (playTestSparkQueue.length) {
        const q = playTestSparkQueue.shift()!;
        burst(q.lane, true);
      }
      // Held rails: a puff every ~70ms until the rail ends or the key is up.
      for (let i = 0; i < FLOW_LANE_COUNT; i++) {
        const hold = playTestHolds[i];
        if (!hold || !liveLaneHeld[i]) { holdSparkClock.current[i] = 0; continue; }
        if (state.currentBeat > hold.endBeat) {      // rode it to the end
          playTestHolds[i] = null;
          holdSparkClock.current[i] = 0;
          burst(i, true);                            // pop at the tail
          continue;
        }
        holdSparkClock.current[i] -= delta;
        if (holdSparkClock.current[i] <= 0) {
          holdSparkClock.current[i] = 0.07;
          burst(i, false);
        }
      }
    }

    // ── Age sparks, then compact the live ones into the instance buffer ──
    // Only `live` instances are drawn, so an idle pool costs nothing and a
    // 1000-spark burst is still a single draw call.
    const dt = Math.min(delta, 0.1);
    const {
      sparkGravity, sparkSize, sparkLifetime: sparkLife, sparkTurbulence,
    } = useFlowStore.getState().particleSettings;
    const inst = sparkInstRef.current;
    const turb = sparkTurbulence ?? 0;
    const tNow = performance.now() / 1000;
    let live = 0;

    for (let i = 0; i < sparksRef.current.length; i++) {
      const s = sparksRef.current[i];
      if (!s.active) continue;
      s.life -= dt;
      if (s.life <= 0) { s.active = false; continue; }

      applyTurbulence(s, turb, tNow, dt);
      s.velocity.y -= sparkGravity * dt;
      s.position.x += s.velocity.x * dt;
      s.position.y += s.velocity.y * dt;
      s.position.z += s.velocity.z * dt;

      // Bounce off the deck. A cube burst throws roughly a third of its sparks
      // downward; without this they sink through the floor and the burst looks
      // like it dies early. Cheap partial restitution — they skitter, not soar.
      if (s.position.y < SPARK_FLOOR) {
        s.position.y = SPARK_FLOOR;
        if (s.velocity.y < 0) s.velocity.y *= -SPARK_BOUNCE;
      }

      if (!inst) continue;
      const fade = s.life / Math.max(0.01, sparkLife);
      const scale = sparkSize * (0.5 + fade * 0.5);
      sparkMat4.makeScale(scale, scale, scale);
      sparkMat4.setPosition(s.position.x, s.position.y, s.position.z);
      inst.setMatrixAt(live, sparkMat4);
      inst.setColorAt(live, s.color);
      sparkAlphaAttr.setX(live, fade);      // per-instance fade (see the shader patch)
      live++;
    }

    if (inst) {
      inst.count = live;
      inst.visible = live > 0;
      if (live > 0) {
        // Upload only the slots actually written. Without a range the whole
        // 2400-instance buffer (150KB of matrices) would go to the GPU every
        // frame even when eight sparks are alive.
        inst.instanceMatrix.clearUpdateRanges();
        inst.instanceMatrix.addUpdateRange(0, live * 16);
        inst.instanceMatrix.needsUpdate = true;
        if (inst.instanceColor) {
          inst.instanceColor.clearUpdateRanges();
          inst.instanceColor.addUpdateRange(0, live * 3);
          inst.instanceColor.needsUpdate = true;
        }
        sparkAlphaAttr.clearUpdateRanges();
        sparkAlphaAttr.addUpdateRange(0, live);
        sparkAlphaAttr.needsUpdate = true;
      }
    }
  });

  const snapDivision = useFlowStore(s => s.snapDivision);
  const stepLinesPlaying = useFlowStore(s => s.playing);

  // Coarse playhead — used to window the grid so we don't render lines and
  // labels for beats that fall off the end of the floor. Re-renders only when
  // the integer beat changes, not on every frame.
  const currentBeatFloor = useFlowStore(s => Math.floor(s.currentBeat));

  /**
   * Build three layers of grid lines, but ONLY within the visible window
   * around the playhead. The world group scrolls with currentBeat, so we
   * keep ~2 beats of margin behind and a full lookahead + margin in front.
   * This prevents labels from floating past the end of the finite floor.
   *   - whole beats (medium weight)
   *   - measure beats every 4 (heavy weight + colored)
   *   - snap subdivisions per beat (thin, sparse)
   */
  const gridLines = useMemo(() => {
    const beats: { z: number; isMeasure: boolean; beatNumber: number }[] = [];
    const subs: { z: number }[] = [];
    const subsPerBeat = Math.max(1, snapDivision / 4);
    const stepSub = 1 / subsPerBeat;

    const startBeat = Math.max(0, currentBeatFloor - 2);
    const endBeat = currentBeatFloor + VISIBLE_BEATS_AHEAD + 1;

    for (let b = startBeat; b <= endBeat; b++) {
      beats.push({ z: beatToZ(b, 0), isMeasure: b % 4 === 0, beatNumber: b });
      if (subsPerBeat > 1) {
        for (let s = 1; s < subsPerBeat; s++) {
          subs.push({ z: beatToZ(b + s * stepSub, 0) });
        }
      }
    }
    return { beats, subs };
  }, [snapDivision, currentBeatFloor, flowNjs]);

  // Accent-themed line colours for the 3D floor (three.js can't read CSS vars,
  // so resolve the live accent hue to hex here; re-memo on theme change).
  const themeAccent = useTheme(s => s.accent);
  const themeAccentHex = useTheme(s => s.accentHex);
  const accentLine = useMemo(() => ({
    outer: accentHex(0.58, 0.17),  // outer lane dividers (stronger)
    inner: accentHex(0.45, 0.13),  // inner lane dividers (dimmer)
    sub:   accentHex(0.82, 0.10),  // snap subdivision lines (light)
    beat:  accentHex(0.80, 0.12),  // whole-beat lines
    measure: accentHex(0.89, 0.085),// measure lines — sidebar section-title tone
    strike: accentHex(0.70, 0.205),// strike/trigger bar — canonical accent token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [themeAccent, themeAccentHex]);

  // Tint-hued dark surfaces for the 3D floor (deck / offset deck).
  const themeTint = useTheme(s => s.tint);
  const themeTintHex = useTheme(s => s.tintHex);
  const deckColors = useMemo(() => ({
    deck:   tintHex(0.10, 0.022),  // main runway deck (near-black, tinted)
    offset: tintHex(0.22, 0.040),  // muted pre-roll / offset zone
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [themeTint, themeTintHex]);

  // ── Note virtualisation ───────────────────────────────────────────────────
  // Only mount Three.js objects for notes inside the visible beat window.
  // The worldRef group scrolls (w.position.z), so a note at beat 400 would
  // still exist as a mesh even though no one can see it — wasteful with 800+
  // chart notes. Re-computed when the integer beat changes (not every frame).
  //
  // Window:
  //   behind  — 2 beats of back-buffer so a note just passed doesn't pop out
  //   ahead   — VISIBLE_BEATS_AHEAD + 4 beats (a little extra for approach)
  //   trails  — use a wider behind-window because a trail can start many beats
  //             before the playhead and still be visible if it ends inside the
  //             window. We include a trail if ANY part of it overlaps the window.
  const WINDOW_BEHIND = 2;
  const WINDOW_AHEAD = VISIBLE_BEATS_AHEAD + 4;
  const TRAIL_BEHIND = 64; // a trail started 4 bars back can still be on-screen

  const visibleNotes = useMemo(() => {
    const lo = currentBeatFloor - WINDOW_BEHIND;
    const hi = currentBeatFloor + WINDOW_AHEAD;
    const inWindow = chart.notes.filter(n => {
      // The note's "end" beat (for trail culling):
      const end = n.time_beats + (n.duration_beats ?? 0);
      // Include if: [note_start, note_end] overlaps [lo, hi]
      return n.time_beats <= hi && end >= lo;
    });
    // Adaptive budget: keep the nearest N (sorted by beat → the first are
    // closest to the playhead; far-ahead notes drop first when the governor
    // lowers the cap on a slow machine).
    return inWindow.length > PERF.maxNotes ? inWindow.slice(0, PERF.maxNotes) : inWindow;
  }, [chart.notes, currentBeatFloor]);

  // Separate pre-pass for trails: they need a wider look-behind window so
  // a trail that started far back but hasn't ended yet stays visible.
  const visibleTrailNotes = useMemo(() => {
    const lo = currentBeatFloor - TRAIL_BEHIND;
    const hi = currentBeatFloor + WINDOW_AHEAD;
    return chart.notes.filter(n => {
      if (!n.duration_beats && !(n.waypoints && n.waypoints.length >= 2)) return false;
      const end = n.time_beats + (n.duration_beats ?? 0);
      return n.time_beats <= hi && end >= lo;
    });
  }, [chart.notes, currentBeatFloor]);

  const handleFloorClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    // Ignore clicks that are the tail-end of an alt-drag — R3F fires onClick
    // on the floor when the dragged object moved away before pointerup.
    if (altDragJustEndedRef.current) return;
    if (e.nativeEvent.altKey) return;
    const state = useFlowStore.getState();
    // Free camera: releasing an orbit/pan drag lands on the floor and must never
    // place a note (user feedback: free-cam release kept dropping stray notes).
    if (state.cameraView === 'free') return;
    // Play Test: the deck is being PLAYED, not edited.
    if (state.playTest) { state.warnEditBlocked(); return; }

    // Empty-floor click while something is selected/cut → DESELECT in one click,
    // no matter where on the deck it lands (offset zone, between notes, any
    // lane). handleFloorClick only fires on the bare floor — notes consume
    // their own clicks — so reaching here always means "clicked empty space".
    if (
      state.noteTypeBrush !== 'trail'
      && !state.pendingTrailNoteId
      && (state.selection.noteIds.size > 0 || state.cutNoteIds.size > 0)
    ) {
      if (state.cutNoteIds.size > 0) state.clearCut();
      if (state.selection.noteIds.size > 0) state.clearSelection();
      return;
    }

    const point = e.point;
    const unit = unitPerBeat();
    const beat = state.currentBeat + point.z / unit;
    // Beat < 0 is the offset (pre-roll) zone — no notes allowed there. Flag it
    // so the editor can flash a "can't place here" message.
    if (beat < 0) { state.warnOffsetPlacement(); return; }

    const subsPerBeat = state.snapDivision / 4;
    const step = 1 / subsPerBeat;
    const snappedBeat = Math.max(0, Math.round(beat / step) * step);
    // Edit-window lock (E): only the beats around the playhead are editable, so
    // a stray click far up the runway can't drop a note into a finished section.
    if (state.editWindowEnabled && Math.abs(snappedBeat - state.currentBeat) > EDIT_WINDOW_BEATS) {
      state.warnEditBlocked();
      return;
    }

    const x = point.x;
    // Mirror the click X back to lane index to match laneCenterX(), spread for
    // the active lane count (the deck widens past 4 lanes).
    const lc = laneCountFor(state.chart, state.activeDifficulty);
    const dw = deckWidthFor(lc);
    if (x < -dw / 2 || x > dw / 2) return;
    const lane = Math.min(
      lc - 1,
      Math.max(0, Math.floor((dw / 2 - x) / (dw / lc))),
    ) as FlowLaneIndex;

    onPlace(lane, snappedBeat, e.nativeEvent.shiftKey || shiftHeldRef.current);
  }, [onPlace]);

  const handleNoteClick = useCallback((e: ThreeEvent<MouseEvent>, note: FlowNote) => {
    e.stopPropagation();
    r3fHitRef.current = true; // a note consumed this click → don't empty-deselect
    const state = useFlowStore.getState();
    // Play Test / edit-window lock — same guards as placement, so a click can't
    // delete a note you're only meant to be playing (or one far off-screen).
    if (state.playTest) { state.warnEditBlocked(); return; }
    if (state.editWindowEnabled && Math.abs(note.time_beats - state.currentBeat) > EDIT_WINDOW_BEATS) {
      state.warnEditBlocked();
      return;
    }
    // While a trail is being drawn, clicking a note must NOT delete it.
    // A double-click to finish the trail otherwise lands its second click on
    // the trail's own note head and wipes the whole trail. Instead, route the
    // click into the trail flow: extend to (and absorb) the clicked note. The
    // store rejects same-or-earlier times, so clicking the head/last node is a
    // harmless no-op and the dblclick handler still commits the trail.
    if (state.pendingTrailNoteId) {
      // Ctrl+click DELETES the note even while drawing a trail (user report:
      // "can't delete notes while in Trail Mode") — without the modifier the
      // click still extends the trail as before.
      if ((e.nativeEvent.ctrlKey || e.nativeEvent.metaKey) && note.id !== state.pendingTrailNoteId) {
        shatterNote(state, note);
        removeNotes(withMirror(note.id));
        return;
      }
      state.addTrailWaypoint(note.lane, note.time_beats);
      // Shift+click finishes the trail even when the final click lands on a note.
      if (e.nativeEvent.shiftKey || shiftHeldRef.current) {
        const id = state.pendingTrailNoteId;
        state.commitTrail();
        markTrailTailSpawned(id);
      }
      return;
    }
    shatterNote(state, note);
    const ids = withMirror(note.id);
    const twinId = ids.find(id => id !== note.id);
    if (twinId) {
      const twin = state.chart.notes.find(n => n.id === twinId);
      if (twin) shatterNote(state, twin, true);   // silent — don't double the sound
    }
    removeNotes(ids);
  }, [removeNotes]);

  // Precompute trail endpoints (note + duration → other note that "anchors" it).
  // We don't validate "ends in a note" — the store does that on creation.
  return (
    <>
      <CameraController />
      <ambientLight intensity={1.2} />
      <directionalLight position={[0, 8, 5]} intensity={0.9} color="#FFFFFF" />
      <pointLight position={[0, 6, -3]} intensity={1.4} color="#D946EF" />
      <pointLight position={[0, 3, 30]} intensity={1.2} color="#7C3AED" />
      <pointLight position={[0, 2, 10]} intensity={0.8} color="#FFFFFF" />

      <group ref={worldRef}>
        {/* Floor — receives clicks for placement. Centered far ahead and
            extended along the full runway so the floor never disappears
            from under the lookahead as the playhead advances.
            Very dark purple (almost black) so the lane colors and the
            notes themselves pop against the deck. */}
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, FLOOR_Y, runwayLength / 2]}
          onClick={handleFloorClick}
          onPointerMove={handleFloorPointerMove}
          onPointerOut={handleFloorPointerOut}
        >
          <planeGeometry args={[deckW, runwayLength]} />
          <meshBasicMaterial color={deckColors.deck} />
        </mesh>

        {/* ── Offset (pre-roll) zone ──────────────────────────────────────
            The lead-in before musical beat 0. In beat-space this is the
            region [-offsetBeats, 0], which sits BEHIND beat 0 — i.e. between
            the strike line and the camera (the lower/near part of the view).
            Rendered inside the scrolling world group, so it's visible at the
            start and scrolls away once the song passes the offset. No notes
            allowed here (handleFloorClick rejects beat < 0). */}
        {(() => {
          const offsetBeats = (chart.offset_seconds * chart.bpm) / 60;
          if (!(offsetBeats > 0.02)) return null;
          const unit = unitPerBeat();
          const lenZ = Math.min(offsetBeats * unit, 80); // cap absurd planes
          const centerZ = -lenZ / 2;                     // strike(0) → camera
          // Push the label well toward the camera (further "back" / lower on
          // screen) so its top edge clears the beat-0 line at the strike.
          const labelZ = -Math.min(Math.max(lenZ * 0.72, 3.0), 3.3);
          const ms = Math.round(chart.offset_seconds * 1000);
          return (
            <group key="offset-zone">
              {/* Muted deck for the pre-roll. raycast=null so the floor below
                  still receives the click (which warns + rejects). */}
              <mesh
                position={[0, FLOOR_Y + 0.006, centerZ]}
                rotation={[-Math.PI / 2, 0, 0]}
                raycast={() => null}
              >
                <planeGeometry args={[deckW, lenZ]} />
                <meshBasicMaterial color={deckColors.offset} transparent opacity={0.55} />
              </mesh>
              {/* Bright line at beat 0 (where the first beat starts). */}
              <mesh
                position={[0, FLOOR_Y + 0.028, 0]}
                rotation={[-Math.PI / 2, 0, 0]}
                raycast={() => null}
              >
                <planeGeometry args={[deckW, 0.12]} />
                <meshBasicMaterial color="#D8B4FE" transparent opacity={0.9} />
              </mesh>
              <Html
                position={[0, FLOOR_Y + 0.12, labelZ]}
                center
                transform
                sprite
                distanceFactor={11}
                occlude={false}
                style={{ pointerEvents: 'none' }}
              >
                <div style={offsetZoneLabelStyle}>
                  <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: 1 }}>Offset</div>
                  <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>({ms}ms)</div>
                  <div style={{ fontSize: 8, opacity: 0.6, marginTop: 4 }}>
                    you can't place notes here
                  </div>
                </div>
              </Html>
            </group>
          );
        })()}

        {/* Lane tint strips — disabled per current visual pass; the runway
            reads cleaner with only the colored keypads at the strike line
            calling out the lane palette.
        {Array.from({ length: FLOW_LANE_COUNT }).map((_, i) => (
          <mesh
            key={`lane-${i}`}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[laneCenterX(i as FlowLaneIndex), FLOOR_Y + 0.010, RUNWAY_LENGTH / 2]}
            raycast={() => null}
          >
            <planeGeometry args={[LANE_WIDTH * 0.92, RUNWAY_LENGTH]} />
            <meshBasicMaterial color={FLOW_LANE_COLORS[i]} transparent opacity={0.05} />
          </mesh>
        ))} */}

        {/* Lane divider lines — same long runway. Spread for the active lane count. */}
        {Array.from({ length: laneCount + 1 }).map((_, i) => {
          const x = -deckW / 2 + (deckW / laneCount) * i;
          const isOuter = i === 0 || i === laneCount;
          return (
            <mesh
              key={`div-${i}`}
              position={[x, FLOOR_Y + 0.014, runwayLength / 2]}
              rotation={[-Math.PI / 2, 0, 0]}
              raycast={() => null}
            >
              <planeGeometry args={[0.03, runwayLength]} />
              <meshBasicMaterial
                color={isOuter ? accentLine.outer : accentLine.inner}
                transparent
                opacity={isOuter
                  ? ifaceSettings.dividerOpacity
                  : ifaceSettings.dividerOpacity * 0.45}
              />
            </mesh>
          );
        })}

        {/* Steps — shared StepLines (a group of snap lines ahead of the strike),
            the same component as Synth VR. Replaces the old subdivision/beat
            grid. Positioned at beatToZ(b, 0) so the scroll group scrolls it. */}
        {/* Play Test hides the snap grid outright — you're playing, not editing. */}
        {!playTest && <StepLines
          width={deckW}
          step={snapStepBeats(snapDivision)}
          // Constant 4-beat blanket ahead (1 behind) no matter the division
          // (reviewer ask) — the line-count cap takes over at ultra-fine snaps.
          beatsAhead={4}
          beatsBehind={1}
          beatToZ={(b) => beatToZ(b, 0)}
          getCurrentBeat={() => useFlowStore.getState().currentBeat}
          playing={stepLinesPlaying}
          // User feedback: keep the beat lines visible while the music plays
          // (default). Settings → UI has a toggle to restore the old fade-out.
          fadeWhilePlaying={ifaceSettings.fadeBeatLinesOnPlay ?? false}
          color={accentLine.sub}
          lineWidth={1.5}
          opacity={0.7}
          y={FLOOR_Y + 0.02}
        />}

        {/* Beat numbers — only visible when beatMarkersVisible is on. EVERY
            beat gets a chip (1-by-1 count), in every perf mode: Perf mode
            used to drop the in-between chips to save DOM-sprite cost, but
            mappers count beats off these numbers, so readability wins over
            the handful of sprites. Measures keep the heavier accent look so
            bar starts still read at a glance. */}
        {/* Potato is the one exception: measures only. Each chip is a DOM
            sprite whose CSS transform is recomputed every frame, which is
            real main-thread cost on the machines this tier exists for. */}
        {ifaceSettings.beatMarkersVisible && gridLines.beats
          .filter(g => g.isMeasure || PRESET_FX.beatChipsAllBeats)
          .map((g) => (
          <Html
            key={`label-${g.beatNumber}`}
            // X pushed outward so the chip clears the lane edge.
            // Y dropped another ~20px (≈ 0.10 world units at distanceFactor=10)
            // since the stub + tick beneath the chip are gone — without them
            // the chip looks like it's floating too high.
            position={[-deckW / 2 - 0.55, FLOOR_Y + 0.08, g.z]}
            transform
            sprite
            distanceFactor={g.isMeasure ? 10 : 8}
            occlude={false}
            zIndexRange={[10, 0]}
            style={{ pointerEvents: 'none' }}
          >
            <div style={g.isMeasure ? beatLabelStyle : beatLabelDimStyle}>{g.beatNumber}</div>
          </Html>
        ))}

        {/* Selection floor overlay — sits above the grid lines but below
            notes and trails. The overlay scrolls with the world group so
            it always tracks the correct beat range. */}
        <SelectionFloorOverlay />
        <SectionFloorOverlay />
        <ComplexityFloorOverlay />
        <LoopFloorOverlay />
        {/* Song waveform as a baked sprite down both runway edges (scrolls w/ world).
            Potato skips it: two big transparent planes are pure fill cost on
            an iGPU, and the same information lives in the bottom timeline. */}
        {PRESET_FX.waveformSprite && <WaveformSprite />}
        {/* Shard burst when a note is deleted (lives in beat-space, scrolls w/ world). */}
        <ShatterField />

        {/* Trails (sustained connectors) — rendered before notes so heads
            draw on top. The trail uses a Catmull-Rom spline through all
            waypoints so lane changes arc smoothly instead of zigzagging.
            Single-lane legacy trails (only duration_beats) collapse to a
            straight 2-point curve.
            When trailWidth > TUBE_THRESHOLD the trail renders as a thick
            TubeGeometry cylinder (Magic-Tiles style); otherwise it uses the
            lightweight Line wire ribbon. */}
        {visibleTrailNotes.flatMap(note => {
          const wps = note.waypoints && note.waypoints.length >= 2
            ? note.waypoints
            : (note.duration_beats && note.duration_beats > 0
                ? [
                    { lane: note.lane, time_beats: note.time_beats },
                    { lane: note.lane, time_beats: note.time_beats + note.duration_beats },
                  ] as FlowTrailWaypoint[]
                : null);
          if (!wps) return [];

          const trailWidth = gameplaySettings.trailWidth;

          // ── Trail body — thick tube OR thin line ──────────────────────
          let trailBody: React.ReactNode;

          if (trailWidth > TUBE_THRESHOLD) {
            // Thick 3D tube — geometry is memoized inside TrailTube so it
            // only rebuilds when the note shape or radius changes.
            trailBody = (
              <TrailTube
                key={`trail-${note.id}`}
                note={note}
                laneColors={laneColors}
                trailWidth={trailWidth}
                onRemove={() => {
                  // Don't delete while a trail is being drawn — the finishing
                  // double-click can land here and would wipe the trail.
                  if (useFlowStore.getState().pendingTrailNoteId) return;
                  removeNotes(withMirror(note.id));
                }}
              />
            );
          } else {
            // Thin wire ribbon — Catmull-Rom sampled to a polyline with a
            // per-vertex lane-color gradient.
            const ctrl = wps.map(w => new THREE.Vector3(
              laneCenterX(w.lane),
              FLOOR_Y + 0.05,
              beatToZ(w.time_beats, 0),
            ));
            const curve = new THREE.CatmullRomCurve3(ctrl, false, 'centripetal');
            const samples = Math.max(32, (wps.length - 1) * 16);
            const points = curve.getPoints(samples).map(p => p.toArray() as [number, number, number]);

            const wpColors = wps.map(w => new THREE.Color(laneColors[w.lane] ?? FLOW_LANE_COLORS[w.lane]));
            const vertexColors = points.map((_, i) => {
              const t = i / Math.max(1, points.length - 1);
              const segF = t * (wps.length - 1);
              const segIdx = Math.min(Math.floor(segF), wps.length - 2);
              const localT = segF - segIdx;
              return wpColors[segIdx].clone().lerp(wpColors[segIdx + 1], localT);
            });

            trailBody = (
              <Line
                key={`trail-${note.id}`}
                points={points}
                vertexColors={vertexColors}
                lineWidth={trailWidth}
                worldUnits
                transparent
                opacity={0.65}
                onClick={(e: import('@react-three/fiber').ThreeEvent<MouseEvent>) => {
                  e.stopPropagation();
                  if (useFlowStore.getState().pendingTrailNoteId) return;
                  removeNotes(withMirror(note.id));
                }}
              />
            );
          }

          const out: React.ReactNode[] = [trailBody];

          // Intermediate waypoint handles — editor-only (Alt-drag). Hidden
          // during playback; first and last nodes always stay visible.
          if (!playing) {
            for (let i = 1; i < wps.length - 1; i++) {
              const w = wps[i];
              const wpIndex = i;
              out.push(
                <mesh
                  key={`trail-${note.id}-wp-${i}`}
                  position={[laneCenterX(w.lane), FLOOR_Y + 0.07, beatToZ(w.time_beats, 0)]}
                  onPointerDown={(e) => {
                    // Plain drag moves the node too (Alt still works) — before,
                    // a drag attempt without Alt fell through to the click
                    // handler and deleted the ENTIRE trail (user report). The
                    // drag is ARMED here and only activates after movement, so
                    // a clean click still reaches onClick below.
                    if (e.nativeEvent.button !== 0) return;
                    e.stopPropagation();
                    onDragArm(note.id, wpIndex, e.nativeEvent.clientX, e.nativeEvent.clientY);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (altDragJustEndedRef.current) return;   // drag, not a click
                    if (useFlowStore.getState().pendingTrailNoteId) return;
                    // Ctrl+click removes JUST this node; a plain click no longer
                    // nukes the whole trail (delete the trail via its body/head).
                    if (e.nativeEvent.ctrlKey || e.nativeEvent.metaKey) {
                      useFlowStore.getState().removeTrailWaypoint(note.id, wpIndex);
                    }
                  }}
                >
                  <capsuleGeometry args={[NOTE_HEIGHT * 0.56, NOTE_HEIGHT * 0.28, 4, 12]} />
                  <meshStandardMaterial
                    color={laneColors[w.lane] ?? FLOW_LANE_COLORS[w.lane]}
                    transparent
                    opacity={0.92}
                    roughness={0.25}
                    metalness={0.15}
                  />
                </mesh>,
              );
            }
          }

          // Tail note — render a note at the trail's CLOSING point (last
          // waypoint) that looks EXACTLY like the head (same Note.fbx model +
          // lane colour) and plays the same drop/bounce entrance (stamped on
          // commit via markTrailTailSpawned). Stays visible during playback
          // just like the start/head note — only the INTERMEDIATE waypoint
          // handles hide on play. Hidden only while THIS trail is still being
          // drawn (so it can't intercept the closing click).
          if (pendingTrailNoteId !== note.id) {
            const tail = wps[wps.length - 1];
            const tailColor = laneColors[tail.lane] ?? FLOW_LANE_COLORS[tail.lane];
            out.push(
              <NoteEntrance
                key={`trail-${note.id}-tail`}
                noteId={`${note.id}#tail`}
                position={[laneCenterX(tail.lane), FLOOR_Y + NOTE_HEIGHT / 2 + gameplaySettings.noteHeight, beatToZ(tail.time_beats, 0)]}
                onClick={(e) => {
                  e.stopPropagation();
                  if (altDragJustEndedRef.current) return;   // drag, not a click
                  const st = useFlowStore.getState();
                  if (st.pendingTrailNoteId) return;
                  // Flick brush on a trail's END places the flick there instead
                  // of deleting the whole trail (user report: "cannot place
                  // flicks on the ends of rails — it deletes the entire rail").
                  const brush = st.noteTypeBrush;
                  if (brush === 'arrow-left' || brush === 'arrow-center' || brush === 'arrow-right') {
                    st.addNote(tail.lane, tail.time_beats, brush);
                    return;
                  }
                  removeNotes(withMirror(note.id));
                }}
                onPointerDown={(e) => {
                  if (e.nativeEvent.button !== 0) return;
                  if (!(note.waypoints && note.waypoints.length >= 2)) return;
                  e.stopPropagation();
                  onDragArm(note.id, note.waypoints.length - 1, e.nativeEvent.clientX, e.nativeEvent.clientY);
                }}
              >
                <Suspense fallback={
                  <RoundedBox args={[LANE_WIDTH * 0.7, NOTE_HEIGHT, NOTE_LENGTH * gameplaySettings.noteScale]} radius={0.05} smoothness={4}>
                    <meshStandardMaterial
                      color={tailColor}
                      emissive={tailColor}
                      emissiveIntensity={gameplaySettings.noteEmissive}
                      roughness={gameplaySettings.noteRoughness}
                      metalness={gameplaySettings.noteMetalness}
                    />
                  </RoundedBox>
                }>
                  <NoteModel
                    color={tailColor}
                    emissiveIntensity={gameplaySettings.noteEmissive}
                    metalness={gameplaySettings.noteMetalness}
                    roughness={gameplaySettings.noteRoughness}
                    noteScale={gameplaySettings.noteScale}
                  />
                </Suspense>
              </NoteEntrance>,
            );
          }

          return out;
        })}

        {/* Shared adaptive perf governor — tunes the object budget to the FPS. */}
        <AdaptivePerfGovernor />
        {/* Potato: hold the render at 30fps (scene logic still runs full rate).
            Mounted conditionally — a priority>0 useFrame would disable
            auto-render for every other mode. */}
        {PRESET_FPS_CAP > 0 && !BENCHMARKING && <PotatoFrameCap cap={PRESET_FPS_CAP} />}

        {/* Notes — always visible; intermediate trail waypoints are hidden
            during playback but first/last nodes stay on. */}
        {visibleNotes.map((note) => {
          const x = laneCenterX(note.lane);
          const z = beatToZ(note.time_beats, 0);
          const color = laneColors[note.lane] ?? FLOW_LANE_COLORS[note.lane];
          const isSelected = selectedIds.has(note.id);
          const arrow = arrowDirectionFromType(note.type);
          const isPending = pendingTrailNoteId === note.id;
          // Cut notes (Ctrl+X) render at 25% opacity so the user can see
          // exactly what the next paste will pick up and move.
          const isCut = cutNoteIds.has(note.id);
          const bodyOpacity = isCut ? 0.25 : 1;
          // Alt + pointerdown on a note starts a drag — we hijack the
          // event before the click bubbles (so it doesn't fire a delete).
          const onAltDownNote = (e: ThreeEvent<PointerEvent>) => {
            if (!e.nativeEvent.altKey) return;
            e.stopPropagation();
            onAltDragStart(note.id, null);
          };
          return (
            <NoteEntrance
              key={note.id}
              noteId={note.id}
              position={[x, FLOOR_Y + NOTE_HEIGHT / 2 + gameplaySettings.noteHeight, z]}
              onClick={(e) => {
                // Suppress the delete-on-click when the user was Alt-dragging.
                if (e.nativeEvent.altKey) { e.stopPropagation(); return; }
                handleNoteClick(e, note);
              }}
              onPointerDown={onAltDownNote}
            >
              {/* Generous invisible hitbox so distant / small notes can
                  still be clicked precisely. */}
              <mesh
                onClick={(e) => {
                  if (e.nativeEvent.altKey) { e.stopPropagation(); return; }
                  handleNoteClick(e, note);
                }}
                onPointerDown={onAltDownNote}
              >
                <boxGeometry args={[LANE_WIDTH * 1.0, NOTE_HEIGHT * 3, NOTE_LENGTH * 2.2]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
              </mesh>

              {/* Note body — Note.fbx mesh with lane colour applied.
                  Falls back to the pill geometry while the FBX loads
                  (should be instant after module-level preload). */}
              <Suspense fallback={
                <RoundedBox args={[LANE_WIDTH * 0.7, NOTE_HEIGHT, NOTE_LENGTH * gameplaySettings.noteScale]} radius={0.05} smoothness={4}>
                  <meshStandardMaterial
                    color={color}
                    emissive={color}
                    emissiveIntensity={isCut ? 0.1 : (isPending ? gameplaySettings.noteEmissive + 0.3 : gameplaySettings.noteEmissive)}
                    roughness={gameplaySettings.noteRoughness}
                    metalness={gameplaySettings.noteMetalness}
                    transparent={isCut}
                    opacity={bodyOpacity}
                  />
                </RoundedBox>
              }>
                <NoteModel
                  color={color}
                  emissiveIntensity={isCut ? 0.1 : gameplaySettings.noteEmissive}
                  metalness={gameplaySettings.noteMetalness}
                  roughness={gameplaySettings.noteRoughness}
                  noteScale={gameplaySettings.noteScale}
                  transparent={isCut}
                  opacity={bodyOpacity}
                />
              </Suspense>

              {/* Direction arrow — only for arrow-* notes. Triangle sits
                  flat on top of the note's box, rotated toward the lane's
                  left / forward / right depending on the type. */}
              {arrow && (
                <ArrowGlyph direction={arrow} dimmed={isCut} />
              )}

              {/* Small white sphere floating above selected notes */}
              {isSelected && (
                <mesh position={[0, NOTE_HEIGHT / 2 + 0.10, 0]} raycast={() => null}>
                  <sphereGeometry args={[0.05, 12, 8]} />
                  <meshBasicMaterial color="#FFFFFF" />
                </mesh>
              )}
              {/* No pending-trail indicator overlay — the note body already
                  renders in its lane colour, so the trail's first note reads as
                  a normal note (the old flat bar on top was removed). */}
            </NoteEntrance>
          );
        })}

        {/* Placement preview — a trigger-area ring on the snapped cell under
            the cursor, marking where a click will drop a note. In Mirror mode a
            second ring shows the mirrored lane too. Hidden during playback and
            while the (Shift) closing ring is showing. */}
        {hoverCell && !trailClose && !playing && ifaceSettings.placeMarkerVisible !== false &&
          (noteMirror
            ? Array.from(new Set([hoverCell.lane, (laneCount - 1 - hoverCell.lane) as FlowLaneIndex]))
            : [hoverCell.lane]
          ).map(lane => (
            <group key={`hover-${lane}`} position={[laneCenterX(lane), FLOOR_Y + 0.045, beatToZ(hoverCell.beat, 0)]}>
              <Line
                points={KEYPAD_OUTLINE_POINTS}
                color={laneColors[lane] ?? FLOW_LANE_COLORS[lane]}
                lineWidth={2.5}
                transparent
                opacity={ifaceSettings.placeMarkerOpacity ?? 0.7}
                raycast={() => null}
              />
            </group>
          ))}

        {/* Trail closing ring — pill outline under the cursor (Shift held)
            marking where a Shift+click will drop the trail's final note. */}
        {trailClose && (
          <group position={[laneCenterX(trailClose.lane), FLOOR_Y + 0.05, beatToZ(trailClose.beat, 0)]}>
            <Line
              points={KEYPAD_OUTLINE_POINTS}
              color={laneColors[trailClose.lane] ?? FLOW_LANE_COLORS[trailClose.lane]}
              lineWidth={4}
              transparent
              opacity={1}
              raycast={() => null}
            />
            <mesh rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
              <shapeGeometry args={[KEYPAD_PILL_SHAPE]} />
              <meshBasicMaterial
                color={laneColors[trailClose.lane] ?? FLOW_LANE_COLORS[trailClose.lane]}
                transparent
                opacity={0.18}
                side={THREE.DoubleSide}
                depthWrite={false}
              />
            </mesh>
          </group>
        )}
      </group>

      {/* Strike bar — stays at world Z = STRIKE_Z. */}
      {ifaceSettings.strikeBarVisible && (
        <mesh
          position={[0, FLOOR_Y + 0.030, STRIKE_Z]}
          rotation={[-Math.PI / 2, 0, 0]}
          raycast={() => null}
        >
          <planeGeometry args={[deckW, ifaceSettings.strikeBarThickness]} />
          <meshBasicMaterial color={ifaceSettings.strikeBarUseAccent !== false ? accentLine.strike : (ifaceSettings.strikeBarColor ?? '#FFFFFF')} transparent opacity={0.85} />
        </mesh>
      )}

      {/* Lane keypads at the strike line — smooth pill OUTLINES (not filled) */}
      {ifaceSettings.keypadVisible !== false && Array.from({ length: laneCount }).map((_, i) => (
        <group
          key={`key-${i}`}
          ref={(el: THREE.Group | null) => { keypadGroups.current[i] = el; }}
          position={[laneCenterX(i as FlowLaneIndex), FLOOR_Y + 0.034, STRIKE_Z]}
        >
          <Line
            ref={(el: any) => { keypadMats.current[i] = el; }}
            points={KEYPAD_OUTLINE_POINTS}
            color={laneColors[i] ?? FLOW_LANE_COLORS[i]}
            lineWidth={2.5}
            transparent
            opacity={ifaceSettings.keypadOpacity}
            raycast={() => null}
          />
        </group>
      ))}

      {/* Spark pool — ONE instanced draw call for the whole burst. `count` is
          set to the live particle total every frame, so an idle pool is free. */}
      <instancedMesh
        ref={sparkInstRef}
        args={[sparkGeo, sparkMat, MAX_SPARKS]}
        frustumCulled={false}
        visible={false}
        raycast={() => null}
      />
    </>
  );
}

// ── Public component ────────────────────────────────────────────────────────

export default function FlowScene() {
  const addNote = useFlowStore(s => s.addNote);
  const noteTypeBrush = useFlowStore(s => s.noteTypeBrush);
  const seek = useFlowStore(s => s.seek);
  const startTrail = useFlowStore(s => s.startTrail);
  const commitTrail = useFlowStore(s => s.commitTrail);
  const cancelTrail = useFlowStore(s => s.cancelTrail);
  const pendingTrailNoteId = useFlowStore(s => s.pendingTrailNoteId);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  // Captured in <Canvas onCreated> — used for screen-space projection
  // when computing right-drag selection hits.
  const cameraRef = useRef<THREE.Camera | null>(null);

  // Right-drag selection rectangle. Stored in CSS pixels relative to the
  // canvas wrapper. null means no drag in progress.
  const [selBox, setSelBox] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  // Used to be a gate behind a "click anywhere" overlay (r3f v9 first-paint
  // workaround). Defaulted to true now — the SceneContents forceTick effect
  // covers the first-paint nudge on its own, and arriving straight in the
  // editor matters more than the cosmetic overlay.
  const [woke] = useState(true);

  // Shift+wheel area-select on the highway. The gesture lasts as long as Shift
  // is held: a moving cursor beat sweeps with the wheel, and the selected range
  // is the UNION of everywhere swept (min..max), so going forward THEN back
  // keeps the whole area instead of shrinking it. Released on Shift keyup.
  const selActiveRef = useRef(false);
  const selCursorRef = useRef(0);
  const selMinRef = useRef(0);
  const selMaxRef = useRef(0);

  // ── Mouse-wheel scrubbing on the canvas ──────────────────────────────
  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper || !woke) return;
    const onWheel = (e: WheelEvent) => {
      const s = useFlowStore.getState();
      // In free camera mode, OrbitControls owns the wheel for zoom — bail
      // out so we don't fight it (and so the user can frame their shot).
      if (s.cameraView === 'free') return;
      e.preventDefault();
      // Scored run: the wheel can't scrub (or fiddle with snap) — it's a
      // play-through. (Practice mode scrubs freely; the store re-arms notes.)
      if (s.playTest && s.playTestScored) return;

      // Shift + wheel → select an AREA instead of scrubbing. Anchored at the
      // playhead on the first tick, each tick grows/shrinks the beat range by
      // one beat and selects every note inside it (matches the timeline's
      // Shift+scroll). The playhead stays put so the area can grow freely.
      if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
        // Start a fresh gesture at the playhead the first tick after Shift went
        // down (selActive is cleared on Shift keyup).
        if (!selActiveRef.current) {
          selActiveRef.current = true;
          selCursorRef.current = s.currentBeat;
          selMinRef.current = s.currentBeat;
          selMaxRef.current = s.currentBeat;
        }
        const dir = e.deltaY < 0 ? 1 : -1;   // scroll up = forward
        selCursorRef.current = Math.max(0, selCursorRef.current + dir);
        // Union: extend the covered range to include the new cursor position.
        selMinRef.current = Math.min(selMinRef.current, selCursorRef.current);
        selMaxRef.current = Math.max(selMaxRef.current, selCursorRef.current);
        const lo = selMinRef.current, hi = selMaxRef.current;
        const ids = s.chart.notes
          .filter(n => n.time_beats >= lo && n.time_beats <= hi)
          .map(n => n.id);
        s.selectNotes(ids, false);
        // Move the playhead to the cursor so the highway scrolls and the user
        // sees the area sweep + the notes light up as they're included.
        const spb = 60 / Math.max(s.chart.bpm, 1);
        seek(selCursorRef.current * spb + s.chart.offset_seconds);
        playScrollTick();
        return;
      }
      // Any non-shift wheel also ends the gesture (belt-and-suspenders with keyup).
      selActiveRef.current = false;

      // Ctrl/Cmd + wheel → cycle the snap division instead of scrubbing.
      if (e.ctrlKey || e.metaKey) {
        const idx = SNAP_OPTIONS.indexOf(s.snapDivision);
        if (idx === -1) return;
        // Scroll UP = finer grid (bigger division number), DOWN = coarser.
        const next = e.deltaY < 0 ? idx + 1 : idx - 1;
        if (next < 0 || next >= SNAP_OPTIONS.length) return;
        s.setSnapDivision(SNAP_OPTIONS[next]);
        playScrollTick();
        return;
      }

      // Inverted scrub: scroll UP advances, DOWN rewinds (matches SR).
      // Step size follows the active snap division — finer grid = finer
      // wheel movement, coarse grid = bigger jumps. Always lands on grid.
      // Uses seek() (not setCurrentTime) so the audio element is moved too,
      // otherwise the rAF loop overwrites the scrub on the next frame.
      const dir = e.deltaY > 0 ? -1 : +1;
      const stepBeats = snapStepBeats(s.snapDivision);
      const snappedCurrent = Math.round(s.currentBeat / stepBeats) * stepBeats;
      const newBeat = Math.max(0, snappedCurrent + dir * stepBeats);
      const secondsPerBeat = 60 / Math.max(s.chart.bpm, 1);
      // Offset-aware: beat 0 lands at audio time = offset_seconds.
      seek(newBeat * secondsPerBeat + s.chart.offset_seconds);
      playScrollTick();
    };
    wrapper.addEventListener('wheel', onWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', onWheel);
  }, [woke, seek]);

  // Releasing Shift ends the Shift+wheel area-select gesture, so the next time
  // the user holds Shift and scrolls it starts a fresh area at the playhead.
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => { if (e.key === 'Shift') selActiveRef.current = false; };
    window.addEventListener('keyup', onKeyUp);
    return () => window.removeEventListener('keyup', onKeyUp);
  }, []);

  // ── Keyboard finalize for trails ──────────────────────────────────────
  // Esc discards the pending trail (head note + waypoints). The trail
  // commits with Shift + click on the final waypoint (handled in handlePlace).
  // Enter also commits, as a keyboard-friendly fallback.
  useEffect(() => {
    if (!pendingTrailNoteId) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelTrail();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const id = useFlowStore.getState().pendingTrailNoteId;
        commitTrail();
        markTrailTailSpawned(id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingTrailNoteId, cancelTrail, commitTrail]);

  // ── Alt + drag → move a note or a trail waypoint ──────────────────────
  // The interaction is started by an Alt+pointerdown on a note (mesh-level
  // handler set on the note JSX) which calls `beginAltDrag(noteId,
  // waypointIndex)`. From there we own window pointermove + pointerup and
  // raycast each frame against an invisible floor plane to translate the
  // cursor's screen position into (lane, beat). Snap-to-grid uses the
  // active snapDivision so trails always land cleanly.
  const altDragRef = useRef<
    { noteId: string; waypointIndex: number | null } | null
  >(null);
  // Set to true for one rAF after a drag ends so handleFloorClick can
  // ignore the spurious onClick R3F fires when the dragged object has moved
  // out from under the cursor before pointerup.
  const altDragJustEndedRef = useRef(false);
  // True when a click hit an R3F object (a note); the canvas-level empty-space
  // deselect reads it so clicking a note doesn't also clear the selection.
  const r3fHitRef = useRef(false);

  // ── Click empty space (anywhere on the canvas, incl. the dark sides off the
  //    runway) → deselect in one click. The floor mesh handles clicks on the
  //    deck; this covers the bare background where no mesh is hit.
  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;
    let downX = 0, downY = 0, downBtn = -1;
    const onDown = (e: PointerEvent) => {
      downX = e.clientX; downY = e.clientY; downBtn = e.button;
      r3fHitRef.current = false; // reset; set true if a note's onClick fires
    };
    const onClick = (e: MouseEvent) => {
      if (downBtn !== 0) return;                                   // left only
      if (Math.abs(e.clientX - downX) > 5 || Math.abs(e.clientY - downY) > 5) return; // a drag, not a click
      if (r3fHitRef.current) return;                               // hit a note → leave it to handleNoteClick
      const s = useFlowStore.getState();
      if (s.cameraView === 'free') return;                         // orbiting
      if (s.noteTypeBrush === 'trail' || s.pendingTrailNoteId) return;
      if (s.cutNoteIds.size > 0) s.clearCut();
      if (s.selection.noteIds.size > 0) s.clearSelection();
    };
    wrapper.addEventListener('pointerdown', onDown);
    wrapper.addEventListener('click', onClick);
    return () => {
      wrapper.removeEventListener('pointerdown', onDown);
      wrapper.removeEventListener('click', onClick);
    };
  }, []);

  const beginAltDrag = useCallback(
    (noteId: string, waypointIndex: number | null) => {
      const s = useFlowStore.getState();
      altDragRef.current = { noteId, waypointIndex };
      // One history snapshot covers the whole drag so undo rewinds in one
      // step instead of N pointermove ticks.
      s.beginEdit();
    },
    [],
  );

  // Deferred drag: trail waypoint/tail handles ARM here on plain pointerdown
  // and the drag only ACTIVATES after real pointer movement (>4px). A no-move
  // press stays a CLICK (so Ctrl+click node-delete and tail clicks still fire)
  // and never pushes a pointless undo snapshot.
  const altDragArmRef = useRef<{ noteId: string; wp: number | null; x: number; y: number } | null>(null);
  const armAltDrag = useCallback(
    (noteId: string, waypointIndex: number | null, x: number, y: number) => {
      altDragArmRef.current = { noteId, wp: waypointIndex, x, y };
    },
    [],
  );

  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;

    const tmpPoint = new THREE.Vector3();
    const tmpMouse = new THREE.Vector2();
    const tmpRay = new THREE.Raycaster();
    const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -FLOOR_Y);

    const onPointerMove = (e: PointerEvent) => {
      // Activate an ARMED trail-handle drag once the pointer really moves.
      const armed = altDragArmRef.current;
      if (armed && !altDragRef.current) {
        if (Math.abs(e.clientX - armed.x) > 4 || Math.abs(e.clientY - armed.y) > 4) {
          beginAltDrag(armed.noteId, armed.wp);
        }
      }
      const drag = altDragRef.current;
      if (!drag) return;
      const camera = cameraRef.current;
      if (!camera) return;
      const rect = wrapper.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      // Screen → NDC → ray → floor intersection (world coords).
      tmpMouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      tmpMouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      tmpRay.setFromCamera(tmpMouse, camera);
      if (!tmpRay.ray.intersectPlane(floorPlane, tmpPoint)) return;

      // World coords → (lane, beat). The runway scrolls inside worldRef
      // by -currentBeat*unit, so the local Z under the cursor is
      // (worldZ + currentBeat*unit). Dividing by `unit` gives the absolute
      // beat under the cursor.
      const state = useFlowStore.getState();
      const unit = unitPerBeat();
      const localZ = tmpPoint.z + state.currentBeat * unit;
      const rawBeat = localZ / unit;
      const stepBeats = 4 / Math.max(state.snapDivision, 1);
      const snappedBeat = Math.max(0, Math.round(rawBeat / stepBeats) * stepBeats);

      const x = tmpPoint.x;
      const lc = laneCountFor(state.chart, state.activeDifficulty);
      const dw = deckWidthFor(lc);
      const laneRaw = Math.floor((dw / 2 - x) / (dw / lc));
      const lane = Math.min(lc - 1, Math.max(0, laneRaw)) as FlowLaneIndex;

      if (drag.waypointIndex !== null) {
        state.moveWaypoint(drag.noteId, drag.waypointIndex, lane, snappedBeat);
      } else {
        state.moveNote(drag.noteId, lane, snappedBeat);
      }
    };

    const onPointerUp = () => {
      if (altDragRef.current !== null) {
        // Block the spurious floor onClick that fires this frame.
        altDragJustEndedRef.current = true;
        requestAnimationFrame(() => { altDragJustEndedRef.current = false; });
      }
      altDragRef.current = null;
      altDragArmRef.current = null;   // an armed-but-unmoved press stays a click
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, []);

  // ── Right-click + drag → multi-select rectangle ───────────────────────
  // Behaviour:
  //   - Right mouse button down on the canvas starts a selection drag.
  //   - As the user drags, an HTML overlay rect tracks the live area.
  //   - On release, every note whose projected screen position falls
  //     inside the rect joins the selection (replaces, not adds —
  //     hold Shift to additive-merge with the existing selection).
  //   - The native browser context menu is suppressed so the drag works.
  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;

    const localPoint = (e: PointerEvent) => {
      const r = wrapper.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const THRESH = 5; // px a LEFT press must move before it's a box-select
    let active: null | 'left' | 'right' = null;
    let moved = false;
    /** Selection mode chosen at drag-start based on modifier keys.
     *    'replace'  — no modifier; clear the previous selection
     *    'add'      — Shift; union with previous selection
     *    'subtract' — Ctrl/Cmd; remove dragged notes from the selection */
    let mode: 'replace' | 'add' | 'subtract' = 'replace';
    let start = { x: 0, y: 0 };
    // Right-drag loop state: the beat where the loop drag began.
    let loopStartBeat = 0;
    const loopRay = new THREE.Raycaster();
    const loopMouse = new THREE.Vector2();
    const loopPt = new THREE.Vector3();
    const loopPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -FLOOR_Y);

    /** Map a local canvas point to the absolute (snapped) beat under the cursor
     *  by raycasting onto the floor — same model as note placement. */
    const screenToBeat = (p: { x: number; y: number }): number | null => {
      const camera = cameraRef.current;
      const rect = wrapper.getBoundingClientRect();
      if (!camera || rect.width === 0 || rect.height === 0) return null;
      loopMouse.x = (p.x / rect.width) * 2 - 1;
      loopMouse.y = -(p.y / rect.height) * 2 + 1;
      loopRay.setFromCamera(loopMouse, camera);
      if (!loopRay.ray.intersectPlane(loopPlane, loopPt)) return null;
      // Only the runway counts as "the timeline" — ignore the dark side areas
      // off the lanes so a loop can't be started/extended out there.
      if (Math.abs(loopPt.x) > CUR_DECK_W / 2) return null;
      const st = useFlowStore.getState();
      const unit = unitPerBeat();
      const rawBeat = (loopPt.z + st.currentBeat * unit) / unit;
      const step = 4 / Math.max(st.snapDivision, 1);
      return Math.max(0, Math.round(rawBeat / step) * step);
    };

    const setMode = (e: PointerEvent) => {
      if (e.shiftKey) mode = 'add';
      else if (e.ctrlKey || e.metaKey) mode = 'subtract';
      else mode = 'replace';
    };

    // Project every note to screen space and select those inside the box.
    const commit = (p: { x: number; y: number }) => {
      const minX = Math.min(start.x, p.x), maxX = Math.max(start.x, p.x);
      const minY = Math.min(start.y, p.y), maxY = Math.max(start.y, p.y);
      const state = useFlowStore.getState();
      const camera = cameraRef.current;
      const rect = wrapper.getBoundingClientRect();
      if (!camera || rect.width === 0 || rect.height === 0) return;
      const unit = unitPerBeat();
      const v = new THREE.Vector3();
      const hits: string[] = [];
      for (const note of state.chart.notes) {
        v.set(
          laneCenterX(note.lane),
          FLOOR_Y + NOTE_HEIGHT / 2,
          beatToZ(note.time_beats, 0) - state.currentBeat * unit,
        );
        v.project(camera);
        if (v.z < -1 || v.z > 1) continue; // behind the camera
        const sx = (v.x * 0.5 + 0.5) * rect.width;
        const sy = (1 - (v.y * 0.5 + 0.5)) * rect.height;
        if (sx >= minX && sx <= maxX && sy >= minY && sy <= maxY) hits.push(note.id);
      }
      if (mode === 'subtract') state.deselectNotes(hits);
      else state.selectNotes(hits, mode === 'add');
    };

    const onPointerDown = (e: PointerEvent) => {
      const s = useFlowStore.getState();
      if (e.button === 2) {
        // Right-drag: define the LOOP region (not note-select). Only arm when
        // the press is ON the runway — off-runway (the dark sides) does nothing,
        // so you can't start a loop while standing outside the timeline.
        const startBeat = screenToBeat(localPoint(e));
        if (startBeat == null) return;
        e.preventDefault();
        active = 'right'; moved = false;
        start = localPoint(e);
        loopStartBeat = startBeat;
      } else if (
        e.button === 0 && !e.altKey && s.cameraView !== 'free'
        // Never arm box-select while a trail is IN PROGRESS: the Shift+click
        // that closes it must reach the floor (a Shift-armed 'add' box-select
        // would otherwise swallow the click). Note: only gated while a trail is
        // pending — the trail BRUSH on its own still allows box-select.
        && !s.pendingTrailNoteId
      ) {
        // Left press: candidate box-select. Don't commit to it until the
        // pointer moves past THRESH — a plain click still places a note (or
        // hits a note). Skipped in free-cam (OrbitControls owns left-drag).
        active = 'left'; moved = false; setMode(e);
        start = localPoint(e);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!active) return;
      const p = localPoint(e);
      if (active === 'right') {
        // Update the loop region live as a snapped beat range.
        if (Math.abs(p.x - start.x) > THRESH || Math.abs(p.y - start.y) > THRESH) moved = true;
        e.preventDefault();
        const endBeat = screenToBeat(p);
        if (endBeat != null && endBeat !== loopStartBeat) {
          useFlowStore.getState().setLoopRegion({
            startBeat: Math.min(loopStartBeat, endBeat),
            endBeat: Math.max(loopStartBeat, endBeat),
          });
        }
        return;
      }
      if (active === 'left' && !moved &&
          (Math.abs(p.x - start.x) > THRESH || Math.abs(p.y - start.y) > THRESH)) {
        moved = true;
      }
      if (moved) {
        e.preventDefault();
        setSelBox({ x0: start.x, y0: start.y, x1: p.x, y1: p.y });
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!active) return;
      const which = active;
      active = null;
      const p = localPoint(e);
      setSelBox(null);
      const state = useFlowStore.getState();

      if (which === 'right' && e.button === 2) {
        // A plain right-click (no drag) toggles the loop OFF.
        if (!moved) { state.clearLoopRegion(); return; }
        // Otherwise the loop region was already set live during the drag.
        return;
      }
      if (which === 'left' && e.button === 0) {
        if (!moved) return; // plain click → let the floor place / the note's click run
        commit(p);
        // Suppress the floor onClick that fires right after this drag so it
        // doesn't also place a note where the drag ended.
        altDragJustEndedRef.current = true;
        requestAnimationFrame(() => { altDragJustEndedRef.current = false; });
      }
    };

    const onContextMenu = (e: MouseEvent) => e.preventDefault();

    wrapper.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    wrapper.addEventListener('contextmenu', onContextMenu);
    return () => {
      wrapper.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      wrapper.removeEventListener('contextmenu', onContextMenu);
    };
  }, []);

  const handlePlace = useCallback((lane: FlowLaneIndex, beat: number, shiftKey = false) => {
    const state = useFlowStore.getState();
    const subsPerBeat = state.snapDivision / 4;
    // "Same spot" is generous so trail mode can find the head/tail note
    // even when the user's click landed slightly off-axis. For plain
    // placement we only refuse to *overlap* an existing note — we never
    // delete from a floor click; deletion is reserved for direct note
    // clicks.
    const tolerance = (1 / subsPerBeat) * 0.5;
    const existingAtSpot = state.chart.notes.find(
      n => n.lane === lane && Math.abs(n.time_beats - beat) < tolerance,
    );

    // Empty-floor click while a selection is active clears the selection
    // instead of placing a note. Lets the user "click off" to dismiss the
    // highlight without picking up a Ctrl key. We bail out *before* the
    // trail logic too — trails-mid-flight still progress because they go
    // through their own `pendingTrailNoteId` check above this point.
    const hasSelection = state.selection.noteIds.size > 0;
    const hasCut = state.cutNoteIds.size > 0;
    if (
      noteTypeBrush !== 'trail'
      && !pendingTrailNoteId
      && !existingAtSpot
      && (hasSelection || hasCut)
    ) {
      if (hasCut) state.clearCut();
      if (hasSelection) state.clearSelection();
      return;
    }

    // Trail mode — multi-waypoint flow:
    //   1st click           → create a HEAD note + seed waypoints[0].
    //   subsequent clicks   → add a waypoint at (lane, beat).
    //   Shift + click       → add the FINAL waypoint and commit the trail.
    //   Enter               → commit the trail (handled by the keyboard listener).
    //   ESC                 → cancel and discard the head note.
    if (noteTypeBrush === 'trail') {
      if (!pendingTrailNoteId) {
        // Start the trail. If the click landed on an existing tap, use it
        // as the head — lets the user "attach" a trail to a note already
        // in the chart. Otherwise spawn a fresh tap and seed the trail.
        if (existingAtSpot) {
          startTrail(existingAtSpot.id);
        } else {
          addNote(lane, beat, 'tap');
          markNoteSpawned(lane, beat);
          playPlaceSound();
          const justAdded = useFlowStore.getState().chart.notes
            .find(n => n.lane === lane && Math.abs(n.time_beats - beat) < tolerance);
          if (justAdded) startTrail(justAdded.id);
        }
        return;
      }
      // Trail in progress — every floor click extends it with a new
      // waypoint. The store rejects same-or-earlier times so the trail
      // always moves forward in time.
      state.addTrailWaypoint(lane, beat);
      // Shift + click places the final waypoint AND finishes the trail.
      if (shiftKey) {
        const id = state.pendingTrailNoteId;
        state.commitTrail();
        markTrailTailSpawned(id);
      }
      return;
    }

    // Non-trail brushes: place or remove.
    if (existingAtSpot) {
      // Same brush → remove (toggle). In Mirror mode also remove the twin.
      shatterNote(state, existingAtSpot);
      const ids = withMirror(existingAtSpot.id);
      const twinId = ids.find(id => id !== existingAtSpot.id);
      if (twinId) {
        const twin = state.chart.notes.find(n => n.id === twinId);
        if (twin) shatterNote(state, twin, true);
      }
      state.removeNotes(ids);
      return;
    }
    addNote(lane, beat, noteTypeBrush);
    markNoteSpawned(lane, beat);
    playPlaceSound();
    // Mirror mode → also drop a twin across the centre line (0↔3, 1↔2), unless
    // a note already sits at the mirror spot.
    if (state.noteMirror) {
      const lc = laneCountFor(state.chart, state.activeDifficulty);
      const mirrorLane = (lc - 1 - lane) as FlowLaneIndex;
      const dup = state.chart.notes.find(n => n.lane === mirrorLane && Math.abs(n.time_beats - beat) < tolerance);
      if (!dup) {
        addNote(mirrorLane, beat, noteTypeBrush);
        markNoteSpawned(mirrorLane, beat);
      }
    }
  }, [addNote, noteTypeBrush, pendingTrailNoteId, startTrail]);

  // Adaptive render resolution, applied through the PROP (see useScaledDpr —
  // an imperative setDpr() fights R3F and flashes the canvas on every round).
  // The active preset's dprCap (user-editable) pins the ratio before the
  // render scale applies; its antialias flag is literal here (Flow has no
  // renderMode axis).
  const presetAa = useEditorStore(s => s.performanceSettings[s.perfMode].antialias);
  const presetDprCap = useEditorStore(s => s.performanceSettings[s.perfMode].dprCap);
  const dpr = useScaledDpr([1, 2], presetDprCap ?? undefined);

  return (
    <div ref={canvasWrapperRef} style={wrapperStyle}>
      <Canvas
        // antialias is a CONTEXT-CREATION flag: the only way to change MSAA
        // (the biggest single win on integrated GPUs) is a fresh context, so
        // the canvas is deliberately remounted when the resolved aa flips.
        // (dpr is a live prop — never key on it.)
        key={presetAa ? 'aa' : 'no-aa'}
        camera={{
          position: [0, 3.0, -4],
          fov: 60,
          near: 0.1,
          far: 400,
        }}
        // preserveDrawingBuffer removed: nothing reads this canvas back
        // (verified — no toDataURL/toBlob/captureStream outside viz/), and it
        // forces a buffer COPY instead of a swap every frame — a fill-rate tax
        // that hits exactly the weak iGPUs users report 30fps on.
        // high-performance: dual-GPU laptops otherwise run the editor on the
        // integrated chip while the discrete GPU idles (telemetry finding).
        gl={{ antialias: presetAa, alpha: true, powerPreference: 'high-performance' }}
        dpr={dpr}
        flat
        frameloop="always"
        onCreated={({ camera, gl, invalidate }) => {
          // Look at a point closer to the strike bar so the keypads, the
          // strike line and the nearest grid lines all stay in frame.
          camera.lookAt(0, FLOOR_Y + 0.3, 12);
          gl.setClearColor('#000000', 0);
          cameraRef.current = camera;
          invalidate();
        }}
        style={{ ...canvasStyle, background: 'transparent' }}
      >
        <Suspense fallback={null}>
          <SceneContents onPlace={handlePlace} onAltDragStart={beginAltDrag} onDragArm={armAltDrag} altDragJustEndedRef={altDragJustEndedRef} r3fHitRef={r3fHitRef} />
        </Suspense>
      </Canvas>

      {/* Top banner: contextual hint while placing a trail. The song info card
          now lives in the right sidebar (see FlowApp), so the canvas top is
          otherwise left clear. */}
      {pendingTrailNoteId && (
        <div style={hintStyle}>
          Click to add points · hold Shift to aim the closing ring, click to finish · ESC to cancel
        </div>
      )}

      {selBox && (
        <div
          style={{
            ...selectionRectStyle,
            left:  Math.min(selBox.x0, selBox.x1),
            top:   Math.min(selBox.y0, selBox.y1),
            width: Math.abs(selBox.x1 - selBox.x0),
            height: Math.abs(selBox.y1 - selBox.y0),
          }}
        />
      )}

    </div>
  );
}

const wrapperStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  // Transparent so the full-screen background (gradient + particle visual +
  // imported visualizer, rendered fixed behind the shell) shows through behind
  // the highway — the bg fills 100% of the screen, not just the centre column.
  background: 'transparent',
};

const canvasStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  // Sit above the backdrop gradient + interactive visual layers (which are
  // absolutely positioned in highwayHolder), so the highway/notes draw on top
  // while the particle visual glows behind them.
  position: 'relative',
  zIndex: 2,
};

const selectionRectStyle: React.CSSProperties = {
  position: 'absolute',
  border: '1.5px solid rgba(217, 70, 239, 0.85)',
  background: 'rgba(217, 70, 239, 0.10)',
  borderRadius: 2,
  pointerEvents: 'none',
  zIndex: 5,
};

const hintStyle: React.CSSProperties = {
  position: 'absolute',
  // Pinned to the top of the canvas instead of the bottom so it doesn't
  // crowd the strike line / keypads / nearest notes.
  top: 8,
  left: '50%',
  transform: 'translateX(-50%)',
  fontSize: 10,
  color: color.inkDim,
  background: 'rgba(10, 5, 32, 0.75)',
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid rgba(120, 80, 220, 0.15)',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
};

const beatLabelStyle: React.CSSProperties = {
  fontFamily: font.sans,
  // Styled like the transport play button (accent outline + text, dark tinted
  // fill, pill corners) so it stays tied to the general theme.
  fontSize: 7,
  fontWeight: 700,
  color: 'var(--accent)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  padding: '2px 7px',
  borderRadius: 999,
  border: '1px solid var(--accent)',
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
  userSelect: 'none',
};

// Plain (non-measure) beats are numbered too — 1-by-1 count, all editors —
// with a lighter chip so bar starts still pop.
const beatLabelDimStyle: React.CSSProperties = {
  ...beatLabelStyle,
  color: 'var(--accent-dim)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.45)',
  fontWeight: 600,
};

const offsetZoneLabelStyle: React.CSSProperties = {
  fontFamily: font.sans,
  color: '#E9D5FF',
  textAlign: 'center',
  textShadow: '0 2px 10px rgba(0,0,0,0.7)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
};

